import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { confirmDialog } from "primereact/confirmdialog";
import { v4 as uuidv4 } from "uuid";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { RouterAddress } from "../../../constants/RouterAddress";
import { AI_MESSAGE_FOR_COMPLIANCE, getErrorTitle, JOB_STATUS, RESIDENT_CARE_PLAN_TYPE, USER_GROUP_ADMIN } from "../../../constants/constants";
import { ROLE_TYPE_ADMIN, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { openUrlInNewTab } from "../../../utils/securityUtils";
import { calculateDeepAge, capitalizeFirstLetter } from "../../../utils/stringUtils";
import CarePlanContactsDialog, { validateCarePlanContactsForm } from "../components/CarePlanContactsDialog";
import {
    dashboardPageContainerStyle,
    renderDashboardHeader,
} from "../../dashboard/pages/dashboardStyles";
import {
    appAccentPanelStyle,
    appDialogContentStyle,
    appDialogHeaderStyle,
    appDialogStyle,
    appFormSectionStyle,
    appInnerCardStyle,
    appMutedTextStyle,
    appNeutralPanelStyle,
    appSectionCardStyle,
    renderDialogFooter,
    renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "txt", "png", "jpg", "jpeg", "tif", "tiff", "mp3", "wav", "m4a"];
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_STAGED_FILES = 10;
const DOC_TAG_OPTIONS = [
    { label: "Face Sheet", value: "FaceSheet" },
    { label: "MAR Sheet", value: "MARSheet" },
    { label: "Assessment Notes", value: "AssessmentNotes" },
];
const NURSE_SECTION_LABELS: Record<string, string> = {
    "STEP#01": "Resident Info & Demographics",
    "STEP#02": "Emergency Evacuation",
    "STEP#03": "Dementia Screening",
    "STEP#04": "Current Medical Diagnosis",
    "STEP#05": "Currently Prescribed Medications",
    "STEP#06": "Psychosocial Profile",
    "STEP#07": "Mental Health Screening",
    "STEP#08": "Cognitive Assessment",
    "STEP#09": "Sensory & Communication",
    "STEP#10": "Medical Systems Review",
    "STEP#11": "Activities of Daily Living",
    "STEP#12": "Behavioral Observations",
    "STEP#13": "Signatures",
};
const DOC_TAG_TO_SECTION_RECORD_TYPES: Record<string, string[]> = {
    FaceSheet: ["STEP#01"],
    MARSheet: ["STEP#05"],
    AssessmentNotes: ["STEP#02", "STEP#03", "STEP#04", "STEP#06", "STEP#07", "STEP#08", "STEP#09", "STEP#10", "STEP#11", "STEP#12"],
};
interface StagedFile {
    id: string;
    file: File;
    tags: string[];
}

interface UploadProgressState {
    active: boolean;
    status: "idle" | "uploading" | "uploaded" | "failed";
    totalFiles: number;
    completedFiles: number;
    successfulFiles: number;
    currentFileName: string;
    failedFiles: string[];
    errorMessage: string;
}

type SharedResidentProfileFormState = {
    firstName: string;
    lastName: string;
    active: boolean;
    isOnMedicaid: boolean;
    medicaidClientId: string;
    providerOneNumber: string;
    primaryInsuranceName: string;
    primaryInsurancePlanType: string;
    primaryInsuranceMemberId: string;
    primaryInsuranceGroupNumber: string;
    primaryInsuranceSubscriberName: string;
    primaryInsuranceSubscriberRelationship: string;
    primaryInsurancePhone: string;
    primaryInsuranceClaimsPhone: string;
    secondaryInsuranceName: string;
    secondaryInsurancePlanType: string;
    secondaryInsuranceMemberId: string;
    secondaryInsuranceGroupNumber: string;
    secondaryInsuranceSubscriberName: string;
    secondaryInsuranceSubscriberRelationship: string;
    secondaryInsurancePhone: string;
    secondaryInsuranceClaimsPhone: string;
    insuranceNotes: string;
};

const DEFAULT_UPLOAD_PROGRESS: UploadProgressState = {
    active: false,
    status: "idle",
    totalFiles: 0,
    completedFiles: 0,
    successfulFiles: 0,
    currentFileName: "",
    failedFiles: [],
    errorMessage: "",
};

const getAutomationCreditsBadgeStyle = (availableCredits: number) => ({
    background: availableCredits > 0 ? "#ecfdf3" : "#fff7ed",
    border: availableCredits > 0 ? "1px solid #bbf7d0" : "1px solid #fed7aa",
    color: availableCredits > 0 ? "#166534" : "#9a3412",
    borderRadius: 999,
    padding: "0.45rem 0.85rem",
    fontWeight: 700,
    whiteSpace: "nowrap" as const,
});

const getNurseUsageLeftLabel = (remainingThisPeriod: number) =>
    `Automation Left This Billing Period: ${remainingThisPeriod}`;

const dialogMetricLabelStyle = {
    fontSize: "0.78rem",
    fontWeight: 800,
    color: "#64748b",
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
};

const getStatusChipStyle = (tone: "success" | "warning" | "neutral") => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    padding: "0.25rem 0.65rem",
    fontSize: "0.78rem",
    fontWeight: 700,
    background: tone === "success" ? "#dcfce7" : tone === "warning" ? "#fff7ed" : "#f8fafc",
    color: tone === "success" ? "#166534" : tone === "warning" ? "#9a3412" : "#334155",
    border: tone === "success" ? "1px solid #86efac" : tone === "warning" ? "1px solid #fed7aa" : "1px solid #cbd5e1",
});

const startDraftDialogStyle = {
    ...appDialogStyle,
    width: "min(96vw, 58rem)",
};

const startDraftDialogContentStyle = {
    ...appDialogContentStyle,
    maxHeight: "80vh",
    overflowY: "auto" as const,
};

const compactProfilePanelStyle = {
    border: "1px solid #dbeafe",
    borderRadius: "18px",
    background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
    padding: "0.95rem 1rem",
} as const;

const compactProfileCardStyle = {
    ...appSectionCardStyle,
    padding: "0.95rem",
    borderRadius: 18,
    height: "100%",
} as const;

const compactProfileGridStyle = {
    rowGap: "0.45rem",
} as const;

const compactProfileFieldCardStyle = {
    padding: "0.55rem 0.7rem",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    background: "#ffffff",
    minHeight: "100%",
} as const;

const compactProfileLabelStyle = {
    fontWeight: 700,
    color: "#334155",
    marginBottom: "0.1rem",
    fontSize: "0.78rem",
    lineHeight: 1.25,
} as const;

const compactProfileValueStyle = {
    color: "#0f172a",
    wordBreak: "break-word" as const,
    lineHeight: 1.35,
    fontSize: "0.92rem",
} as const;

const buildSharedResidentProfileFormState = (residentProfile: any): SharedResidentProfileFormState => ({
    firstName: String(residentProfile?.firstName || ""),
    lastName: String(residentProfile?.lastName || ""),
    active: residentProfile?.active !== false,
    isOnMedicaid: residentProfile?.isOnMedicaid === true,
    medicaidClientId: String(residentProfile?.medicaidClientId || ""),
    providerOneNumber: String(residentProfile?.providerOneNumber || ""),
    primaryInsuranceName: String(residentProfile?.primaryInsuranceName || ""),
    primaryInsurancePlanType: String(residentProfile?.primaryInsurancePlanType || ""),
    primaryInsuranceMemberId: String(residentProfile?.primaryInsuranceMemberId || ""),
    primaryInsuranceGroupNumber: String(residentProfile?.primaryInsuranceGroupNumber || ""),
    primaryInsuranceSubscriberName: String(residentProfile?.primaryInsuranceSubscriberName || ""),
    primaryInsuranceSubscriberRelationship: String(residentProfile?.primaryInsuranceSubscriberRelationship || ""),
    primaryInsurancePhone: String(residentProfile?.primaryInsurancePhone || ""),
    primaryInsuranceClaimsPhone: String(residentProfile?.primaryInsuranceClaimsPhone || ""),
    secondaryInsuranceName: String(residentProfile?.secondaryInsuranceName || ""),
    secondaryInsurancePlanType: String(residentProfile?.secondaryInsurancePlanType || ""),
    secondaryInsuranceMemberId: String(residentProfile?.secondaryInsuranceMemberId || ""),
    secondaryInsuranceGroupNumber: String(residentProfile?.secondaryInsuranceGroupNumber || ""),
    secondaryInsuranceSubscriberName: String(residentProfile?.secondaryInsuranceSubscriberName || ""),
    secondaryInsuranceSubscriberRelationship: String(residentProfile?.secondaryInsuranceSubscriberRelationship || ""),
    secondaryInsurancePhone: String(residentProfile?.secondaryInsurancePhone || ""),
    secondaryInsuranceClaimsPhone: String(residentProfile?.secondaryInsuranceClaimsPhone || ""),
    insuranceNotes: String(residentProfile?.insuranceNotes || ""),
});

const getFirstNonEmptyString = (...values: any[]) => {
    for (const value of values) {
        const normalized = String(value || "").trim();
        if (normalized) {
            return normalized;
        }
    }
    return "";
};

const getFirstDefinedBoolean = (...values: any[]) => {
    for (const value of values) {
        if (typeof value === "boolean") {
            return value;
        }
    }
    return undefined;
};

const NurseResidentProfilePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { residentId, homeId } = useParams();
    const { authStore, backendAPIStore } = useContext(MobXProviderContext);
    const toast = useRef<Toast>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pollingTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const settledBatchStatuses = useRef<Record<string, string>>({});
    const automationStartInFlightRef = useRef<boolean>(false);
    const residentProfileImageRefreshAttemptsRef = useRef<Set<string>>(new Set());

    const [loadingProfile, setLoadingProfile] = useState<boolean>(true);
    const [savingProfile, setSavingProfile] = useState<boolean>(false);
    const [archivingResident, setArchivingResident] = useState<boolean>(false);
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [residentProfile, setResidentProfile] = useState<any | null>(null);
    const [profileForm, setProfileForm] = useState<SharedResidentProfileFormState>(buildSharedResidentProfileFormState(null));
    const [profileFormErrors, setProfileFormErrors] = useState<Record<string, string>>({});
    const [profileError, setProfileError] = useState<string | null>(null);
    const [residentProfileImageUrl, setResidentProfileImageUrl] = useState<string>("");
    const [carePlanResponsiblePartiesData, setCarePlanResponsiblePartiesData] = useState<any>(null);
    const [carePlanMedicationManagementData, setCarePlanMedicationManagementData] = useState<any>(null);
    const [carePlanHealthIndicatorData, setCarePlanHealthIndicatorData] = useState<any>(null);
    const [carePlanSummaryData, setCarePlanSummaryData] = useState<any>(null);
    const [carePlanContactsFormData, setCarePlanContactsFormData] = useState<any>({});
    const [carePlanContactsErrors, setCarePlanContactsErrors] = useState<Record<string, string>>({});
    const [savingCarePlanContacts, setSavingCarePlanContacts] = useState<boolean>(false);
    const [isEditingCarePlanContacts, setIsEditingCarePlanContacts] = useState<boolean>(false);
    const [carePlanBasicsFormData, setCarePlanBasicsFormData] = useState<any>({ anyAllergies: false, allergiesDescribed: "" });
    const [carePlanBasicsErrors, setCarePlanBasicsErrors] = useState<Record<string, string>>({});
    const [savingCarePlanBasics, setSavingCarePlanBasics] = useState<boolean>(false);
    const [isEditingCarePlanBasics, setIsEditingCarePlanBasics] = useState<boolean>(false);
    const [automationLoading, setAutomationLoading] = useState<boolean>(true);
    const [automationSubmitting, setAutomationSubmitting] = useState<boolean>(false);
    const [automationDocuments, setAutomationDocuments] = useState<any[]>([]);
    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const [processingJobDetails, setProcessingJobDetails] = useState<any>(null);
    const [showProcessingDialog, setShowProcessingDialog] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<UploadProgressState>(DEFAULT_UPLOAD_PROGRESS);
    const [showFeatureAccessDialog, setShowFeatureAccessDialog] = useState<boolean>(false);
    const [featureAccessDialogMessage, setFeatureAccessDialogMessage] = useState<string>("");
    const [featureAccessDialogAvailableCredits, setFeatureAccessDialogAvailableCredits] = useState<number>(0);
    const [automationCreditsAvailable, setAutomationCreditsAvailable] = useState<number>(0);
    const [featureAccessDialogAllowUseCredit, setFeatureAccessDialogAllowUseCredit] = useState<boolean>(false);
    const [featureAccessDialogUseCreditAction, setFeatureAccessDialogUseCreditAction] = useState<(() => void) | null>(null);
    const [nurseAssessmentsRemainingThisPeriod, setNurseAssessmentsRemainingThisPeriod] = useState<number>(0);
    const [providerCanUseNurseWorkspace, setProviderCanUseNurseWorkspace] = useState<boolean>(false);
    const [assessmentForms, setAssessmentForms] = useState<any[]>([]);
    const [assessmentFormsCount, setAssessmentFormsCount] = useState<number>(0);
    const [delegationFormsCount, setDelegationFormsCount] = useState<number>(0);
    const [footNailCareFormsCount, setFootNailCareFormsCount] = useState<number>(0);
    const [ncpForms, setNcpForms] = useState<any[]>([]);
    const [ncpFormsCount, setNcpFormsCount] = useState<number>(0);
    const [workflowSummaryLoaded, setWorkflowSummaryLoaded] = useState<boolean>(false);
    const [latestAssessmentForm, setLatestAssessmentForm] = useState<any | null>(null);
    const [latestNcpForm, setLatestNcpForm] = useState<any | null>(null);
    const [downloadingAssessment, setDownloadingAssessment] = useState<boolean>(false);
    const [downloadingNcpFormId, setDownloadingNcpFormId] = useState<string>("");
    const [creatingNcpVersion, setCreatingNcpVersion] = useState<boolean>(false);
    const [tagValidationRequested, setTagValidationRequested] = useState<boolean>(false);
    const [showNcpHistoryDialog, setShowNcpHistoryDialog] = useState<boolean>(false);
    const [showAssessmentHistoryDialog, setShowAssessmentHistoryDialog] = useState<boolean>(false);
    const [showNewAutomationDialog, setShowNewAutomationDialog] = useState<boolean>(false);
    const [activeWorkflowTabIndex, setActiveWorkflowTabIndex] = useState<number>(0);
    const [generatingFaceSheet, setGeneratingFaceSheet] = useState<boolean>(false);

    const userRoleType = authStore.getUserRoleType();
    const userGroups = authStore.getUserGroups() || [];
    const isAdminUser = userGroups.includes(USER_GROUP_ADMIN) || userRoleType === ROLE_TYPE_ADMIN;
    const isProviderNurseResidentMode = location.pathname.startsWith("/provider/nurse/");
    const isSharedResidentMode = location.pathname.startsWith("/shared/home/") && location.pathname.includes("/profile/");
    const isProviderResidentMode = location.pathname.startsWith("/home/");
    const generateDelegationDrafts = isProviderNurseResidentMode;
    const usesProviderAssessmentAutomation = isProviderResidentMode || isProviderNurseResidentMode;
    const usesProviderDelegationApi = isProviderResidentMode || isProviderNurseResidentMode;
    const usesNurseNcpWorkspace = !isProviderResidentMode;
    const canUseSharedAssessmentWorkspace = !residentProfile?.sharedAccess || residentProfile?.assessmentAccessEnabled !== false;
    const canUseSharedCarePlanWorkspace = !residentProfile?.sharedAccess || residentProfile?.carePlanAccessEnabled !== false;
    const canUseSharedDelegationWorkspace = !residentProfile?.sharedAccess || residentProfile?.delegationAccessEnabled !== false;
    const canUseSharedBinderWorkspace = !residentProfile?.sharedAccess || residentProfile?.binderAccessEnabled === true;
    const canGenerateMarFromProfile = isProviderResidentMode || isProviderNurseResidentMode || isAdminUser;
    const canShowProviderNurseCredits = userRoleType === ROLE_TYPE_PROVIDER && providerCanUseNurseWorkspace;
    const canShowNurseUsageLeft = userRoleType === ROLE_TYPE_NURSE && !usesProviderAssessmentAutomation;
    const buildCarePlanContactsFormState = useCallback(() => ({
        primaryPhysicianName: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianName,
            residentProfile?.primaryPhysicianName,
        ),
        primaryPhysicianClinic: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianClinic,
            residentProfile?.primaryPhysicianClinic,
        ),
        primaryPhysicianPhone: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianPhone,
            carePlanResponsiblePartiesData?.primaryPhysicianPhoneNumber,
            residentProfile?.primaryPhysicianPhoneNumber,
        ),
        primaryPhysicianFax: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianFax,
            carePlanResponsiblePartiesData?.primaryPhysicianFaxNumber,
            residentProfile?.primaryPhysicianFaxNumber,
        ),
        primaryPhysicianEmail: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianEmail,
            residentProfile?.primaryPhysicianEmail,
        ),
        primaryPhysicianAddress: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianAddress,
            residentProfile?.primaryPhysicianAddress,
        ),
        pharmacyName: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.pharmacyName,
            residentProfile?.pharmacyName,
        ),
        pharmacyPhone: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.pharmacyPhone,
            carePlanResponsiblePartiesData?.pharmacyPhoneNumber,
            residentProfile?.pharmacyPhoneNumber,
        ),
        pharmacyFax: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.pharmacyFax,
            carePlanResponsiblePartiesData?.pharmacyFaxNumber,
            residentProfile?.pharmacyFaxNumber,
        ),
        pharmacyEmail: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.pharmacyEmail,
            residentProfile?.pharmacyEmail,
        ),
        pharmacyAddress: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.pharmacyAddress,
            residentProfile?.pharmacyAddress,
        ),
        collateralContacts: Array.isArray(carePlanResponsiblePartiesData?.collateralContacts)
            ? carePlanResponsiblePartiesData.collateralContacts.map((contact: any) => ({
                fullName: String(contact?.fullName || "").trim(),
                relationship: String(contact?.relationship || "").trim(),
                preferredContactMethod: String(contact?.preferredContactMethod || "").trim(),
                phoneNumber: String(contact?.phoneNumber || "").trim(),
                email: String(contact?.email || "").trim(),
            }))
            : [],
    }), [carePlanResponsiblePartiesData, residentProfile]);
    const buildCarePlanBasicsFormState = useCallback(() => ({
        anyAllergies: !!getFirstDefinedBoolean(
            carePlanMedicationManagementData?.anyAllergies,
            carePlanHealthIndicatorData?.anyAllergies,
        ),
        allergiesDescribed: getFirstNonEmptyString(
            carePlanMedicationManagementData?.allergiesDescribed,
            carePlanHealthIndicatorData?.allergySubstanceDetails,
            carePlanSummaryData?.residentAllergies,
            residentProfile?.allergiesDescribed,
        ),
    }), [carePlanHealthIndicatorData, carePlanMedicationManagementData, carePlanSummaryData, residentProfile]);
    const handleOpenAssessmentVersion = (targetFormId: string) => {
        if (!targetFormId) return;
        setShowAssessmentHistoryDialog(false);
        navigate(`${assessmentPath}?formId=${encodeURIComponent(targetFormId)}`);
    };

    const assessmentPath = isSharedResidentMode
        ? RouterAddress.getSharedResidentAssessmentPagePath(homeId || "", residentId!)
        : isProviderResidentMode && homeId
        ? RouterAddress.getProviderResidentAssessmentPagePath(homeId, residentId!)
        : isProviderNurseResidentMode
            ? RouterAddress.getProviderNurseResidentAssessmentPagePath(residentId!)
            : RouterAddress.getNurseResidentAssessmentPagePath(residentId!);
    const delegationPath = isSharedResidentMode
        ? RouterAddress.getSharedResidentDelegationPagePath(homeId || "", residentId!)
        : isProviderResidentMode && homeId
        ? RouterAddress.getProviderResidentDelegationPagePath(homeId, residentId!)
        : isProviderNurseResidentMode
            ? RouterAddress.getProviderNurseResidentDelegationPagePath(residentId!)
            : RouterAddress.getNurseResidentDelegationPagePath(residentId!);
    const binderPath = isSharedResidentMode
        ? RouterAddress.getSharedResidentBinderPagePath(homeId || "", residentId!)
        : isProviderResidentMode && homeId
        ? RouterAddress.getResidentBinderPagePath(homeId, residentId!)
        : isProviderNurseResidentMode
            ? RouterAddress.getProviderNurseResidentBinderPagePath(residentId!)
            : RouterAddress.getNurseResidentBinderPagePath(residentId!);
    const podiatristPath = (isProviderResidentMode || isProviderNurseResidentMode)
            ? RouterAddress.getProviderNurseResidentPodiatristPagePath(residentId!)
            : RouterAddress.getNurseResidentPodiatristPagePath(residentId!);
    const ncpPath = isSharedResidentMode
        ? RouterAddress.getSharedResidentNcpPagePath(homeId || "", residentId!)
        : isProviderResidentMode && homeId
        ? RouterAddress.getResidentNCPPagePath(homeId, residentId!)
        : isProviderNurseResidentMode
            ? RouterAddress.getProviderNurseResidentNcpPagePath(residentId!)
            : RouterAddress.getNurseResidentNcpPagePath(residentId!);
    const emarPath = RouterAddress.getProviderResidentEmarPagePath(residentId!, homeId);
    const isSubscriptionRequiredError = useCallback((error: any) => {
        const code = String(error?.code || error?.cause?.message || error?.details?.code || "").trim().toUpperCase();
        const message = String(error?.message || error?.details?.error || "").toLowerCase();
        return code === "SUBSCRIPTION_REQUIRED"
            || code === "SUBSCRIPTION_LIMIT_REACHED"
            || message.includes("active subscription is required")
            || message.includes("review billing to continue")
            || message.includes("monthly nurse plans")
            || message.includes("nurse automation credits");
    }, []);

    const openAssessmentSubscriptionDialog = useCallback((message?: string) => {
        setFeatureAccessDialogAvailableCredits(0);
        setFeatureAccessDialogAllowUseCredit(false);
        setFeatureAccessDialogUseCreditAction(null);
        setFeatureAccessDialogMessage(
            String(message || "").trim()
            || "An active subscription is required to continue with nurse assessments. Review Billing to continue."
        );
        setShowFeatureAccessDialog(true);
    }, []);

    const openProviderNurseCreditDialog = useCallback((error?: any, options?: { allowUseCredit?: boolean; onUseCredit?: () => void; }) => {
        const availableCredits = Number(
            error?.automationCreditsAvailable
            || error?.details?.automationCreditsAvailable
            || 0
        );
        const canUseCredit = Boolean(options?.allowUseCredit && availableCredits > 0);
        setFeatureAccessDialogAvailableCredits(availableCredits);
        setAutomationCreditsAvailable(availableCredits);
        setFeatureAccessDialogAllowUseCredit(canUseCredit);
        setFeatureAccessDialogUseCreditAction(() => (canUseCredit && options?.onUseCredit ? options.onUseCredit : null));
        setFeatureAccessDialogMessage(
            availableCredits > 0
                ? "Your monthly draft uses are used up. Use 1 automation credit to continue."
                : (String(error?.message || error?.details?.error || "").trim()
                    || "You do not have any nurse drafting credits right now. Buy Automation Credit to continue.")
        );
        setShowFeatureAccessDialog(true);
    }, []);

    const refreshAssessmentAccess = useCallback(async () => {
        try {
            if (canShowProviderNurseCredits) {
                const billingDetails = await backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_PROVIDER);
                setAutomationCreditsAvailable(
                    Number(
                        billingDetails?.automationCreditsAvailable
                        || billingDetails?.featureCreditBalances?.provider_nurse_assessment
                        || 0
                    )
                );
                return;
            }

            if (canShowNurseUsageLeft) {
                const billingDetails = await backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_NURSE);
                setNurseAssessmentsRemainingThisPeriod(
                    Number(billingDetails?.nurseAssessmentsRemainingThisPeriod || 0)
                );
            }
        } catch (error) {
            console.error("Failed to load nurse assessment billing access", error);
        }
    }, [authStore, backendAPIStore, canShowNurseUsageLeft, canShowProviderNurseCredits]);

    const loadResidentWorkflowSummary = useCallback(async () => {
        if (!residentId || isSharedResidentMode || (isSharedResidentMode && !residentProfile)) return;
        setWorkflowSummaryLoaded(false);
        await backendAPIStore.init();
        try {
            const [assessmentResponse, delegationResponse, ncpResponse, footNailCareResponse] = await Promise.all([
                canUseSharedAssessmentWorkspace
                    ? (usesProviderAssessmentAutomation
                        ? backendAPIStore.getProviderAssessmentForms(residentId)
                        : backendAPIStore.getNurseAssessmentForms(residentId))
                    : Promise.resolve(null),
                canUseSharedDelegationWorkspace
                    ? (usesProviderDelegationApi
                        ? backendAPIStore.getProviderDelegationForms(residentId)
                        : backendAPIStore.getNurseDelegationForms(residentId))
                    : Promise.resolve(null),
                usesNurseNcpWorkspace && canUseSharedCarePlanWorkspace
                    ? backendAPIStore.getNurseNcpForms(residentId)
                    : Promise.resolve(null),
                backendAPIStore.getNursePodiatristForms(residentId),
            ]);

            const assessmentForms = Array.isArray(assessmentResponse?.forms)
                ? assessmentResponse.forms
                : Array.isArray(assessmentResponse)
                    ? assessmentResponse
                    : [];
            const delegationForms = Array.isArray(delegationResponse?.forms)
                ? delegationResponse.forms
                : Array.isArray(delegationResponse)
                    ? delegationResponse
                    : [];
            const delegationFormCodes = new Set(
                delegationForms
                    .map((form: any) => String(form?.formCode || "").trim())
                    .filter(Boolean)
            );
            const nextNcpForms = Array.isArray(ncpResponse?.forms)
                ? ncpResponse.forms
                : Array.isArray(ncpResponse)
                    ? ncpResponse
                    : [];
            const footNailCareForms = Array.isArray(footNailCareResponse?.forms)
                ? footNailCareResponse.forms
                : Array.isArray(footNailCareResponse)
                    ? footNailCareResponse
                    : [];
            const delegationFormCount = delegationFormCodes.size || delegationForms.length;

            const latestAssessment = assessmentForms
                .slice()
                .sort((left: any, right: any) =>
                    String(right?.modifiedAt || right?.createdAt || "").localeCompare(String(left?.modifiedAt || left?.createdAt || ""))
                )[0] || null;
            const latestNcp = nextNcpForms
                .slice()
                .sort((left: any, right: any) =>
                    String(right?.updatedAt || right?.createdAt || "").localeCompare(String(left?.updatedAt || left?.createdAt || ""))
                )[0] || null;

            setAssessmentForms(assessmentForms);
            setAssessmentFormsCount(assessmentForms.length);
            setDelegationFormsCount(delegationFormCount);
            setFootNailCareFormsCount(footNailCareForms.length);
            setLatestAssessmentForm(latestAssessment);
            setNcpForms(nextNcpForms);
            setNcpFormsCount(nextNcpForms.length);
            setLatestNcpForm(latestNcp);
        } finally {
            setWorkflowSummaryLoaded(true);
        }
    }, [backendAPIStore, canUseSharedAssessmentWorkspace, canUseSharedCarePlanWorkspace, canUseSharedDelegationWorkspace, isSharedResidentMode, residentId, residentProfile, usesNurseNcpWorkspace, usesProviderAssessmentAutomation, usesProviderDelegationApi]);

    const deferSecondaryProfileLoad = useCallback((delay = 0) => {
        if (isSharedResidentMode) {
            return;
        }
        window.setTimeout(() => {
            void loadResidentWorkflowSummary();
            void refreshAssessmentAccess();
        }, delay);
    }, [isSharedResidentMode, loadResidentWorkflowSummary, refreshAssessmentAccess]);

    const loadResidentProfile = useCallback(async () => {
        if (!residentId) return;
        setLoadingProfile(true);
        try {
            await backendAPIStore.init();
            setProviderCanUseNurseWorkspace(userRoleType === ROLE_TYPE_PROVIDER);
            const response = isSharedResidentMode
                ? await backendAPIStore.getNurseResidentProfile(residentId, isSharedResidentMode)
                : isProviderResidentMode
                ? await backendAPIStore.getProviderResidentProfile(residentId)
                : await backendAPIStore.getNurseResidentProfile(residentId);
            setResidentProfile(response || null);
            setProfileForm(buildSharedResidentProfileFormState(response));
            setProfileFormErrors({});
            const canLoadCarePlan = !isSharedResidentMode || response?.carePlanAccessEnabled !== false;
            if (canLoadCarePlan) {
                const carePlanItems = await backendAPIStore.getResidentCarePlan(residentId);
                const nextResponsibleParties = carePlanItems.find((item: any) => item?.carePlanType === RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES) || null;
                const nextMedicationManagement = carePlanItems.find((item: any) => item?.carePlanType === RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT) || null;
                const nextHealthIndicators = carePlanItems.find((item: any) => item?.carePlanType === RESIDENT_CARE_PLAN_TYPE.HEALTH_INDICATORS) || null;
                const nextSummary = carePlanItems.find((item: any) => item?.carePlanType === RESIDENT_CARE_PLAN_TYPE.SUMMARY) || null;
                setCarePlanResponsiblePartiesData(nextResponsibleParties);
                setCarePlanMedicationManagementData(nextMedicationManagement);
                setCarePlanHealthIndicatorData(nextHealthIndicators);
                setCarePlanSummaryData(nextSummary);
            } else {
                setCarePlanResponsiblePartiesData(null);
                setCarePlanMedicationManagementData(null);
                setCarePlanHealthIndicatorData(null);
                setCarePlanSummaryData(null);
            }
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Failed to load resident profile.",
                life: 4000,
            });
        } finally {
            setLoadingProfile(false);
        }
    }, [backendAPIStore, isProviderResidentMode, isSharedResidentMode, residentId]);

    const startBatchPolling = useCallback((batchJobId: string, documentIds: string[]) => {
        if (!batchJobId || pollingTimers.current[batchJobId] || settledBatchStatuses.current[batchJobId] || !residentId) return;

        const poll = async () => {
            try {
                const jobDetails = await backendAPIStore.getJobDetails(batchJobId);
                const status: string = jobDetails?.jobStatus || JOB_STATUS.IN_PROGRESS;
                const done = status === JOB_STATUS.COMPLETED || status === JOB_STATUS.FAILED;

                setProcessingJobDetails(jobDetails);

                if (done) {
                    settledBatchStatuses.current[batchJobId] = status;
                }

                setAutomationDocuments((prev) =>
                    prev.map((doc) =>
                        documentIds.includes(doc.documentId)
                            ? { ...doc, jobStatus: status }
                            : doc
                    )
                );

                if (done) {
                    delete pollingTimers.current[batchJobId];
                    await backendAPIStore.init();
            if (!canUseSharedAssessmentWorkspace) {
                setAutomationDocuments([]);
                return;
            }
            const result = usesProviderAssessmentAutomation
                ? await backendAPIStore.getProviderResidentAssessment(residentId)
                : await backendAPIStore.getNurseResidentAssessment(residentId);
                    const docs: any[] = (result?.documents || [])
                        .map((doc: any) => {
                            const settledStatus = doc.jobId ? settledBatchStatuses.current[doc.jobId] : undefined;
                            if (
                                settledStatus
                                && doc.jobStatus !== JOB_STATUS.COMPLETED
                                && doc.jobStatus !== JOB_STATUS.FAILED
                            ) {
                                return { ...doc, jobStatus: settledStatus };
                            }
                            return doc;
                        })
                        .sort((a: any, b: any) => String(b.uploadedAt || "").localeCompare(String(a.uploadedAt || "")));
                    setAutomationDocuments(docs);
                    deferSecondaryProfileLoad();
                } else {
                    pollingTimers.current[batchJobId] = setTimeout(() => { void poll(); }, 5000);
                }
            } catch (error) {
                console.error("Failed to poll automation job", error);
                delete pollingTimers.current[batchJobId];
            }
        };

        pollingTimers.current[batchJobId] = setTimeout(() => { void poll(); }, 5000);
    }, [backendAPIStore, canUseSharedAssessmentWorkspace, deferSecondaryProfileLoad, residentId, usesProviderAssessmentAutomation]);

    const confirmSharedResidentArchive = () => {
        if (!residentId || !residentProfile || !isSharedResidentMode) return;
        const shouldArchive = residentProfile.active === false;
        confirmDialog({
            header: shouldArchive ? "Unarchive Resident" : "Archive Resident",
            message: shouldArchive
                ? (
                    <div>
                        <p className="m-0">
                            Restore <strong>{sharedResidentDisplayName}</strong> to the active resident list?
                        </p>
                    </div>
                )
                : (
                    <div>
                        <p className="mt-0 mb-2">
                            Archive <strong>{sharedResidentDisplayName}</strong> from this shared home?
                        </p>
                        <p className="m-0">
                            The resident will remain available for unarchive and will not be permanently deleted.
                        </p>
                    </div>
                ),
            icon: shouldArchive ? "pi pi-refresh" : "pi pi-archive",
            acceptLabel: shouldArchive ? "Unarchive" : "Archive Resident",
            rejectLabel: "Cancel",
            acceptClassName: shouldArchive ? "p-button-success" : "p-button-danger",
            accept: () => {
                void (async () => {
                    setArchivingResident(true);
                    try {
                        await backendAPIStore.updateNurseProviderResident({
                            residentId,
                            firstName: String(residentProfile.firstName || "").trim(),
                            lastName: String(residentProfile.lastName || "").trim(),
                            active: shouldArchive,
                        });
                        const refreshed = await backendAPIStore.getNurseResidentProfile(residentId, true);
                        setResidentProfile(refreshed || { ...residentProfile, active: shouldArchive });
                        setProfileForm(buildSharedResidentProfileFormState(refreshed || { ...residentProfile, active: shouldArchive }));
                        toast.current?.show({ severity: "success", summary: shouldArchive ? "Resident Unarchived" : "Resident Archived", detail: shouldArchive ? "Resident restored successfully." : "Resident archived successfully.", life: 3000 });
                        if (!shouldArchive) {
                            const dashboardPath = userRoleType === ROLE_TYPE_MHP
                                ? RouterAddress.MHP_HOME
                                : userRoleType === ROLE_TYPE_NURSE
                                    ? RouterAddress.NURSE_HOME
                                    : RouterAddress.PROVIDER_HOME;
                            navigate(dashboardPath, { replace: true });
                        }
                    } catch (error: any) {
                        toast.current?.show({ severity: "error", summary: "Unable to Update Resident", detail: error?.message || "The resident status could not be updated.", life: 4000 });
                    } finally {
                        setArchivingResident(false);
                    }
                })();
            },
        });
    };

    const loadAutomationWorkspace = useCallback(async () => {
        if (!residentId || isSharedResidentMode || (isSharedResidentMode && !residentProfile)) return;
        setAutomationLoading(true);
        try {
            await backendAPIStore.init();
            if (!canUseSharedAssessmentWorkspace) {
                setAutomationDocuments([]);
                return;
            }
            const residentAssessment = usesProviderAssessmentAutomation
                ? await backendAPIStore.getProviderResidentAssessment(residentId)
                : await backendAPIStore.getNurseResidentAssessment(residentId);

            const docs: any[] = (residentAssessment?.documents || [])
                .map((doc: any) => {
                    const settledStatus = doc.jobId ? settledBatchStatuses.current[doc.jobId] : undefined;
                    if (
                        settledStatus
                        && doc.jobStatus !== JOB_STATUS.COMPLETED
                        && doc.jobStatus !== JOB_STATUS.FAILED
                    ) {
                        return { ...doc, jobStatus: settledStatus };
                    }
                    return doc;
                })
                .sort((a: any, b: any) => String(b.uploadedAt || "").localeCompare(String(a.uploadedAt || "")));
            setAutomationDocuments(docs);

            const pendingByBatchJob = new Map<string, string[]>();
            docs.forEach((doc: any) => {
                if (
                    doc.jobId
                    && !settledBatchStatuses.current[doc.jobId]
                    && doc.jobStatus !== JOB_STATUS.COMPLETED
                    && doc.jobStatus !== JOB_STATUS.FAILED
                ) {
                    const ids = pendingByBatchJob.get(doc.jobId) || [];
                    ids.push(doc.documentId);
                    pendingByBatchJob.set(doc.jobId, ids);
                }
            });
            pendingByBatchJob.forEach((documentIds, batchJobId) => {
                startBatchPolling(batchJobId, documentIds);
            });
            deferSecondaryProfileLoad(120);
        } catch (error: any) {
            if (isSubscriptionRequiredError(error)) {
                setAutomationDocuments([]);
                return;
            }
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Failed to load resident automation workspace.",
                life: 4000,
            });
        } finally {
            setAutomationLoading(false);
        }
    }, [backendAPIStore, canUseSharedAssessmentWorkspace, deferSecondaryProfileLoad, isSharedResidentMode, isSubscriptionRequiredError, residentId, residentProfile, startBatchPolling, usesProviderAssessmentAutomation]);

    useEffect(() => {
        void loadResidentProfile();
    }, [loadResidentProfile]);

    useEffect(() => {
        if (isSharedResidentMode) {
            setAutomationLoading(false);
            return;
        }
        void loadAutomationWorkspace();
        return () => {
            Object.values(pollingTimers.current).forEach(clearTimeout);
        };
    }, [isSharedResidentMode, loadAutomationWorkspace]);

    const handleSaveProfile = async () => {
        if (!residentId) return;

        const firstName = profileForm.firstName.trim();
        const lastName = profileForm.lastName.trim();
        const nextProfileFormErrors: Record<string, string> = {};
        const hasPrimaryInsuranceDetails = [
            profileForm.primaryInsuranceName,
            profileForm.primaryInsurancePlanType,
            profileForm.primaryInsuranceMemberId,
            profileForm.primaryInsuranceGroupNumber,
            profileForm.primaryInsuranceSubscriberName,
            profileForm.primaryInsuranceSubscriberRelationship,
            profileForm.primaryInsurancePhone,
            profileForm.primaryInsuranceClaimsPhone,
        ].some((value) => String(value || "").trim());
        const hasSecondaryInsuranceDetails = [
            profileForm.secondaryInsuranceName,
            profileForm.secondaryInsurancePlanType,
            profileForm.secondaryInsuranceMemberId,
            profileForm.secondaryInsuranceGroupNumber,
            profileForm.secondaryInsuranceSubscriberName,
            profileForm.secondaryInsuranceSubscriberRelationship,
            profileForm.secondaryInsurancePhone,
            profileForm.secondaryInsuranceClaimsPhone,
        ].some((value) => String(value || "").trim());

        if (!firstName || !lastName) {
            if (!firstName) {
                nextProfileFormErrors.firstName = "First name is required.";
            }
            if (!lastName) {
                nextProfileFormErrors.lastName = "Last name is required.";
            }
        }

        if (profileForm.isOnMedicaid && !String(profileForm.medicaidClientId || "").trim() && !String(profileForm.providerOneNumber || "").trim()) {
            nextProfileFormErrors.medicaidClientId = "Add Medicaid Client ID or ProviderOne Number.";
            nextProfileFormErrors.providerOneNumber = "Add ProviderOne Number or Medicaid Client ID.";
        }

        if (hasPrimaryInsuranceDetails) {
            if (!String(profileForm.primaryInsuranceName || "").trim()) {
                nextProfileFormErrors.primaryInsuranceName = "Primary insurance carrier is required.";
            }
            if (!String(profileForm.primaryInsuranceMemberId || "").trim()) {
                nextProfileFormErrors.primaryInsuranceMemberId = "Primary insurance member ID is required.";
            }
            if (!String(profileForm.primaryInsuranceSubscriberName || "").trim()) {
                nextProfileFormErrors.primaryInsuranceSubscriberName = "Primary insurance subscriber name is required.";
            }
        }

        if (hasSecondaryInsuranceDetails) {
            if (!String(profileForm.secondaryInsuranceName || "").trim()) {
                nextProfileFormErrors.secondaryInsuranceName = "Secondary insurance carrier is required.";
            }
            if (!String(profileForm.secondaryInsuranceMemberId || "").trim()) {
                nextProfileFormErrors.secondaryInsuranceMemberId = "Secondary insurance member ID is required.";
            }
        }

        if (Object.keys(nextProfileFormErrors).length > 0) {
            setProfileFormErrors(nextProfileFormErrors);
            setProfileError("Fix the highlighted resident profile fields before saving.");
            return;
        }

        setProfileFormErrors({});
        setProfileError(null);
        setSavingProfile(true);
        try {
            await backendAPIStore.init();
            if (isProviderResidentMode) {
                const updatedResident = {
                    ...(residentProfile || {}),
                    residentId,
                    firstName,
                    lastName,
                    active: profileForm.active !== false,
                    isOnMedicaid: profileForm.isOnMedicaid,
                    medicaidClientId: profileForm.medicaidClientId.trim(),
                    providerOneNumber: profileForm.providerOneNumber.trim(),
                    primaryInsuranceName: profileForm.primaryInsuranceName.trim(),
                    primaryInsurancePlanType: profileForm.primaryInsurancePlanType.trim(),
                    primaryInsuranceMemberId: profileForm.primaryInsuranceMemberId.trim(),
                    primaryInsuranceGroupNumber: profileForm.primaryInsuranceGroupNumber.trim(),
                    primaryInsuranceSubscriberName: profileForm.primaryInsuranceSubscriberName.trim(),
                    primaryInsuranceSubscriberRelationship: profileForm.primaryInsuranceSubscriberRelationship.trim(),
                    primaryInsurancePhone: profileForm.primaryInsurancePhone.trim(),
                    primaryInsuranceClaimsPhone: profileForm.primaryInsuranceClaimsPhone.trim(),
                    secondaryInsuranceName: profileForm.secondaryInsuranceName.trim(),
                    secondaryInsurancePlanType: profileForm.secondaryInsurancePlanType.trim(),
                    secondaryInsuranceMemberId: profileForm.secondaryInsuranceMemberId.trim(),
                    secondaryInsuranceGroupNumber: profileForm.secondaryInsuranceGroupNumber.trim(),
                    secondaryInsuranceSubscriberName: profileForm.secondaryInsuranceSubscriberName.trim(),
                    secondaryInsuranceSubscriberRelationship: profileForm.secondaryInsuranceSubscriberRelationship.trim(),
                    secondaryInsurancePhone: profileForm.secondaryInsurancePhone.trim(),
                    secondaryInsuranceClaimsPhone: profileForm.secondaryInsuranceClaimsPhone.trim(),
                    insuranceNotes: profileForm.insuranceNotes.trim(),
                };
                await backendAPIStore.updateResident(updatedResident);
                setResidentProfile(updatedResident);
            } else {
                await backendAPIStore.updateNurseProviderResident({
                    residentId,
                    firstName,
                    lastName,
                    active: profileForm.active !== false,
                    isOnMedicaid: profileForm.isOnMedicaid,
                    medicaidClientId: profileForm.medicaidClientId.trim(),
                    providerOneNumber: profileForm.providerOneNumber.trim(),
                    primaryInsuranceName: profileForm.primaryInsuranceName.trim(),
                    primaryInsurancePlanType: profileForm.primaryInsurancePlanType.trim(),
                    primaryInsuranceMemberId: profileForm.primaryInsuranceMemberId.trim(),
                    primaryInsuranceGroupNumber: profileForm.primaryInsuranceGroupNumber.trim(),
                    primaryInsuranceSubscriberName: profileForm.primaryInsuranceSubscriberName.trim(),
                    primaryInsuranceSubscriberRelationship: profileForm.primaryInsuranceSubscriberRelationship.trim(),
                    primaryInsurancePhone: profileForm.primaryInsurancePhone.trim(),
                    primaryInsuranceClaimsPhone: profileForm.primaryInsuranceClaimsPhone.trim(),
                    secondaryInsuranceName: profileForm.secondaryInsuranceName.trim(),
                    secondaryInsurancePlanType: profileForm.secondaryInsurancePlanType.trim(),
                    secondaryInsuranceMemberId: profileForm.secondaryInsuranceMemberId.trim(),
                    secondaryInsuranceGroupNumber: profileForm.secondaryInsuranceGroupNumber.trim(),
                    secondaryInsuranceSubscriberName: profileForm.secondaryInsuranceSubscriberName.trim(),
                    secondaryInsuranceSubscriberRelationship: profileForm.secondaryInsuranceSubscriberRelationship.trim(),
                    secondaryInsurancePhone: profileForm.secondaryInsurancePhone.trim(),
                    secondaryInsuranceClaimsPhone: profileForm.secondaryInsuranceClaimsPhone.trim(),
                    insuranceNotes: profileForm.insuranceNotes.trim(),
                });
                setResidentProfile((prev: any) => ({
                    ...(prev || {}),
                    firstName,
                    lastName,
                    active: profileForm.active !== false,
                    isOnMedicaid: profileForm.isOnMedicaid,
                    medicaidClientId: profileForm.medicaidClientId.trim(),
                    providerOneNumber: profileForm.providerOneNumber.trim(),
                    primaryInsuranceName: profileForm.primaryInsuranceName.trim(),
                    primaryInsurancePlanType: profileForm.primaryInsurancePlanType.trim(),
                    primaryInsuranceMemberId: profileForm.primaryInsuranceMemberId.trim(),
                    primaryInsuranceGroupNumber: profileForm.primaryInsuranceGroupNumber.trim(),
                    primaryInsuranceSubscriberName: profileForm.primaryInsuranceSubscriberName.trim(),
                    primaryInsuranceSubscriberRelationship: profileForm.primaryInsuranceSubscriberRelationship.trim(),
                    primaryInsurancePhone: profileForm.primaryInsurancePhone.trim(),
                    primaryInsuranceClaimsPhone: profileForm.primaryInsuranceClaimsPhone.trim(),
                    secondaryInsuranceName: profileForm.secondaryInsuranceName.trim(),
                    secondaryInsurancePlanType: profileForm.secondaryInsurancePlanType.trim(),
                    secondaryInsuranceMemberId: profileForm.secondaryInsuranceMemberId.trim(),
                    secondaryInsuranceGroupNumber: profileForm.secondaryInsuranceGroupNumber.trim(),
                    secondaryInsuranceSubscriberName: profileForm.secondaryInsuranceSubscriberName.trim(),
                    secondaryInsuranceSubscriberRelationship: profileForm.secondaryInsuranceSubscriberRelationship.trim(),
                    secondaryInsurancePhone: profileForm.secondaryInsurancePhone.trim(),
                    secondaryInsuranceClaimsPhone: profileForm.secondaryInsuranceClaimsPhone.trim(),
                    insuranceNotes: profileForm.insuranceNotes.trim(),
                }));
            }
            setEditDialogOpen(false);
            toast.current?.show({
                severity: "success",
                summary: "Updated",
                detail: "Resident profile updated successfully.",
                life: 3000,
            });
        } catch (error: any) {
            setProfileError(error?.message || "Unable to update resident profile.");
        } finally {
            setSavingProfile(false);
        }
    };

    const handleFileSelect = (event: any) => {
        const files = Array.from<File>(event.target.files || []);
        const valid: StagedFile[] = [];
        const invalid: string[] = [];
        const remainingSlots = Math.max(0, MAX_STAGED_FILES - stagedFiles.length);
        const overflowFiles: string[] = [];

        files.forEach((file: File, index: number) => {
            const ext = file.name.split(".").pop()?.toLowerCase() || "";
            if (!ALLOWED_EXTENSIONS.includes(ext) || file.size > MAX_FILE_SIZE_BYTES) {
                invalid.push(file.name);
            } else if (index >= remainingSlots) {
                overflowFiles.push(file.name);
            } else {
                valid.push({ id: uuidv4(), file, tags: [] });
            }
        });

        if (invalid.length) {
            toast.current?.show({
                severity: "warn",
                summary: "Skipped files",
                detail: `Unsupported or too large: ${invalid.join(", ")}`,
                life: 5000,
            });
        }

        if (overflowFiles.length) {
            toast.current?.show({
                severity: "warn",
                summary: "Document limit reached",
                detail: `You can add up to ${MAX_STAGED_FILES} documents at a time. Skipped: ${overflowFiles.join(", ")}`,
                life: 5000,
            });
        }

        setTagValidationRequested(false);
        setStagedFiles((prev) => [...prev, ...valid]);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const updateStagedTags = (id: string, tags: string[]) => {
        setTagValidationRequested(false);
        setStagedFiles((prev) => prev.map((file) => file.id === id ? { ...file, tags } : file));
    };

    const toggleStagedTag = (id: string, tag: string) => {
        setTagValidationRequested(false);
        setStagedFiles((prev) => prev.map((file) => {
            if (file.id !== id) return file;
            const hasTag = (file.tags || []).includes(tag);
            return {
                ...file,
                tags: hasTag
                    ? file.tags.filter((currentTag) => currentTag !== tag)
                    : [...(file.tags || []), tag],
            };
        }));
    };

    const removeStagedFile = (id: string) => {
        setStagedFiles((prev) => prev.filter((file) => file.id !== id));
    };

    const hasActiveAssessmentDraftJob = automationDocuments.some((doc: any) =>
        doc.jobStatus === JOB_STATUS.SUBMITTED || doc.jobStatus === JOB_STATUS.IN_PROGRESS
    ) || processingJobDetails?.jobStatus === JOB_STATUS.SUBMITTED || processingJobDetails?.jobStatus === JOB_STATUS.IN_PROGRESS;

    const handleStartAutomation = async (useExistingCredit: boolean = false) => {
        if (!residentId || stagedFiles.length === 0 || automationStartInFlightRef.current) return;
        if (hasActiveAssessmentDraftJob) {
            toast.current?.show({
                severity: "warn",
                summary: "Draft already running",
                detail: "An assessment draft job is already running for this resident. Wait for it to finish before starting another one.",
                life: 4000,
            });
            return;
        }
        if (stagedFiles.some((file) => file.tags.length === 0)) {
            setTagValidationRequested(true);
            return;
        }

        automationStartInFlightRef.current = true;
        setTagValidationRequested(false);
        setAutomationSubmitting(true);
        setShowProcessingDialog(true);
        setProcessingJobDetails(null);
        setUploadProgress({
            active: true,
            status: "uploading",
            totalFiles: stagedFiles.length,
            completedFiles: 0,
            successfulFiles: 0,
            currentFileName: "",
            failedFiles: [],
            errorMessage: "",
        });
        await backendAPIStore.init();

        const uploadedDocs: Array<{ objectKey: string; documentId: string; fileName: string; tag: string; tags: string[] }> = [];
        const failedUploads: string[] = [];
        const failedUploadIds: string[] = [];

        for (const staged of stagedFiles) {
            const documentId = uuidv4();
            const ext = staged.file.name.split(".").pop()?.toLowerCase() || "pdf";
            setUploadProgress((prev) => ({
                ...prev,
                active: true,
                status: "uploading",
                currentFileName: staged.file.name,
            }));
            try {
                const { presignedUrl, objectKey } = usesProviderAssessmentAutomation
                    ? await backendAPIStore.getProviderAssessmentPresignedUrl(residentId, documentId, ext)
                    : await backendAPIStore.getNurseAssessmentPresignedUrl(residentId, documentId, ext);
                await backendAPIStore.uploadFileToS3(presignedUrl, staged.file);
                uploadedDocs.push({
                    objectKey,
                    documentId,
                    fileName: staged.file.name,
                    tag: staged.tags[0] || "AssessmentNotes",
                    tags: staged.tags,
                });
                setUploadProgress((prev) => ({
                    ...prev,
                    completedFiles: prev.completedFiles + 1,
                    successfulFiles: prev.successfulFiles + 1,
                }));
            } catch (error: any) {
                if (usesProviderAssessmentAutomation) {
                    setUploadProgress((prev) => ({
                        ...prev,
                        active: true,
                        status: "failed",
                        completedFiles: prev.completedFiles + 1,
                        failedFiles: [...prev.failedFiles, staged.file.name],
                        errorMessage: String(error?.message || error?.details?.error || "Failed while uploading source files.").trim(),
                        currentFileName: staged.file.name,
                    }));
                    setAutomationSubmitting(false);
                    automationStartInFlightRef.current = false;
                    openProviderNurseCreditDialog(error);
                    return;
                }
                failedUploads.push(staged.file.name);
                failedUploadIds.push(staged.id);
                setUploadProgress((prev) => ({
                    ...prev,
                    completedFiles: prev.completedFiles + 1,
                    failedFiles: [...prev.failedFiles, staged.file.name],
                    errorMessage: String(error?.message || prev.errorMessage || "").trim(),
                }));
            }
        }

        if (uploadedDocs.length > 0) {
            try {
                const createdForm = usesProviderAssessmentAutomation
                    ? await backendAPIStore.createProviderAssessmentForm(residentId)
                    : await backendAPIStore.createNurseAssessmentForm(residentId);
                const nextFormId = String(createdForm?.formId || "").trim() || undefined;
                const result = usesProviderAssessmentAutomation
                    ? await backendAPIStore.createProviderResidentAssessmentJob({
                        residentId,
                        formId: nextFormId,
                        documents: uploadedDocs,
                        generateDelegationDrafts,
                        useExistingCredit,
                        billingAccessWorkspace: isProviderNurseResidentMode ? "provider_nurse" : "provider_resident",
                    })
                    : await backendAPIStore.createNurseAssessmentJob({
                        residentId,
                        formId: nextFormId,
                        documents: uploadedDocs,
                        generateDelegationDrafts,
                        useExistingCredit,
                    });
                const batchJobId = result?.jobId || null;
                const queuedDocs = uploadedDocs.map((doc) => ({
                    ...doc,
                    jobId: batchJobId,
                    formId: result?.formId,
                    assessmentVersion: result?.version,
                    jobStatus: batchJobId ? JOB_STATUS.SUBMITTED : null,
                    uploadedAt: new Date().toISOString(),
                }));
                setAutomationDocuments((prev) => {
                    const docsById = new Map(prev.map((item: any) => [item.documentId, item]));
                    queuedDocs.forEach((doc) => docsById.set(doc.documentId, { ...docsById.get(doc.documentId), ...doc }));
                    return Array.from(docsById.values()).sort((a: any, b: any) =>
                        String(b.uploadedAt || "").localeCompare(String(a.uploadedAt || ""))
                    );
                });
                setProcessingJobDetails({
                    ...(result || {}),
                    jobId: batchJobId,
                    jobStatus: JOB_STATUS.SUBMITTED,
                    createdAt: new Date().toISOString(),
                    delegationDraftGenerationRequested: generateDelegationDrafts,
                });
                setUploadProgress((prev) => ({
                    ...prev,
                    active: true,
                    status: prev.failedFiles.length > 0 ? "failed" : "uploaded",
                    currentFileName: "",
                }));
                setShowProcessingDialog(true);
                if (batchJobId) {
                    startBatchPolling(batchJobId, queuedDocs.map((doc) => doc.documentId));
                }
                await loadResidentWorkflowSummary();
            } catch (error: any) {
                setUploadProgress((prev) => ({
                    ...prev,
                    active: true,
                    status: "failed",
                    currentFileName: "",
                    errorMessage: String(error?.message || error?.details?.error || "Failed to start assessment drafting after uploads completed.").trim(),
                }));
                if (isSubscriptionRequiredError(error)) {
                    if (usesProviderAssessmentAutomation) {
                        openProviderNurseCreditDialog(error, {
                            allowUseCredit: !useExistingCredit,
                            onUseCredit: () => {
                                setShowFeatureAccessDialog(false);
                                void handleStartAutomation(true);
                            },
                        });
                    } else {
                        openAssessmentSubscriptionDialog(error?.message || error?.details?.error);
                    }
                } else {
                    setProcessingJobDetails({
                        jobStatus: JOB_STATUS.FAILED,
                        createdAt: new Date().toISOString(),
                        errorMessage: error?.message || "Failed to start assessment drafting.",
                        delegationDraftGenerationRequested: generateDelegationDrafts,
                    });
                }
                setAutomationSubmitting(false);
                automationStartInFlightRef.current = false;
                return;
            }
        }

        setStagedFiles((prev) => prev.filter((file) => failedUploadIds.includes(file.id)));
        setAutomationSubmitting(false);
        automationStartInFlightRef.current = false;
        if (uploadedDocs.length > 0) {
            setShowNewAutomationDialog(false);
        }

        if (failedUploads.length > 0) {
            setUploadProgress((prev) => ({
                ...prev,
                active: true,
                status: uploadedDocs.length > 0 ? "failed" : "failed",
                currentFileName: "",
                errorMessage: prev.errorMessage || "Some source files could not be uploaded.",
            }));
            return;
        }

        setUploadProgress((prev) => ({
            ...prev,
            active: uploadedDocs.length > 0,
            status: uploadedDocs.length > 0 ? "uploaded" : "idle",
            currentFileName: "",
        }));
    };

    const handleCreateManualAssessment = async () => {
        if (!residentId) return;
        setAutomationSubmitting(true);
        try {
            await backendAPIStore.init();
            const createdForm = usesProviderAssessmentAutomation
                ? await backendAPIStore.createProviderAssessmentForm(residentId)
                : await backendAPIStore.createNurseAssessmentForm(residentId);
            const nextFormId = String(createdForm?.formId || "").trim();
            await loadResidentWorkflowSummary();
            setShowNewAutomationDialog(false);
            navigate(nextFormId ? `${assessmentPath}?formId=${encodeURIComponent(nextFormId)}` : assessmentPath);
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to create a manual assessment.",
                life: 4000,
            });
        } finally {
            setAutomationSubmitting(false);
        }
    };

    const openNewAutomationDialog = () => {
        setShowNewAutomationDialog(true);
    };

    const closeNewAutomationDialog = () => {
        if (automationSubmitting) {
            return;
        }
        setShowNewAutomationDialog(false);
    };

    const editDialogFooter = renderDialogFooter({
        errorMessage: profileError,
        loading: savingProfile,
        onCancel: () => {
            setEditDialogOpen(false);
            setProfileError(null);
            setProfileForm(buildSharedResidentProfileFormState(residentProfile));
            setProfileFormErrors({});
        },
        onSubmit: handleSaveProfile,
        submitLabel: "Save Profile",
    });

    const getSetupWizardHighlightStyle = useCallback((_target: string) => ({}), []);

    const processingDocuments = useMemo(() => automationDocuments.filter((doc: any) =>
        doc.jobStatus === JOB_STATUS.SUBMITTED
        || doc.jobStatus === JOB_STATUS.IN_PROGRESS
        || (doc.jobStatus === JOB_STATUS.FAILED && doc.jobId === processingJobDetails?.jobId)
    ), [automationDocuments, processingJobDetails]);
    const isUploadActive = uploadProgress.active && (uploadProgress.status === "uploading" || uploadProgress.status === "uploaded" || uploadProgress.status === "failed");

    const activeAutomationCount = automationDocuments.filter((doc: any) =>
        doc.jobStatus === JOB_STATUS.SUBMITTED || doc.jobStatus === JOB_STATUS.IN_PROGRESS
    ).length;
    const isProcessingCompleted = processingJobDetails?.jobStatus === JOB_STATUS.COMPLETED;
    const isProcessingFailed = processingJobDetails?.jobStatus === JOB_STATUS.FAILED;
    const nurseSections = Array.isArray(processingJobDetails?.nurseSections)
        ? processingJobDetails.nurseSections
        : [];
    const targetSections = nurseSections.filter((section: any) => !section?.skipped);
    const successfulSections = targetSections.filter((section: any) =>
        section?.status === "completed" || (section?.success && !section?.skipped)
    );
    const completedSectionKeys = new Set(
        successfulSections
            .map((section: any) => String(section?.recordType || "").trim())
            .filter(Boolean)
    );
    const skippedSectionKeys = new Set(
        nurseSections
            .filter((section: any) => section?.skipped)
            .map((section: any) => String(section?.recordType || "").trim())
            .filter(Boolean)
    );
    const taggedSectionKeys = new Set(
        processingDocuments.flatMap((doc: any) => {
            const tags = Array.isArray(doc?.tags)
                ? doc.tags
                : doc?.tag
                    ? [doc.tag]
                    : [];
            return tags.flatMap((tag: string) => DOC_TAG_TO_SECTION_RECORD_TYPES[String(tag || "").trim()] || []);
        })
    );
    const pendingSections = isProcessingCompleted
        ? []
        : Array.from(taggedSectionKeys)
            .filter((recordType) => !completedSectionKeys.has(recordType) && !skippedSectionKeys.has(recordType))
            .filter((recordType) => !!NURSE_SECTION_LABELS[recordType])
            .map((recordType) => ({ recordType, description: NURSE_SECTION_LABELS[recordType] }));
    const generatedDelegationForms = Array.isArray(processingJobDetails?.generatedDelegationForms)
        ? processingJobDetails.generatedDelegationForms
        : [];
    const delegationDraftGenerationRequested = Boolean(processingJobDetails?.delegationDraftGenerationRequested);
    const delegationDraftGenerationStatus = String(processingJobDetails?.delegationDraftGenerationStatus || "").trim().toLowerCase();
    const isDelegationDraftGenerationActiveStatus = delegationDraftGenerationStatus.includes("queue")
        || delegationDraftGenerationStatus.includes("submit")
        || delegationDraftGenerationStatus.includes("progress")
        || delegationDraftGenerationStatus.includes("process")
        || delegationDraftGenerationStatus.includes("generat");
    const isDelegationDraftGenerationInProgress = delegationDraftGenerationRequested
        && isDelegationDraftGenerationActiveStatus
        && generatedDelegationForms.length === 0
        && !delegationDraftGenerationStatus.includes("failed")
        && !delegationDraftGenerationStatus.includes("complete");
    const shouldShowProcessingDialogContent = isUploadActive || processingDocuments.length > 0 || !!processingJobDetails;
    const shouldShowUploadProgressSection = uploadProgress.active && uploadProgress.status !== "uploaded";

    const formatProcessingStatus = (status?: string) => {
        const rawStatus = String(status || "").trim();
        if (!rawStatus) return "Preparing assessment";

        const normalizedStatus = rawStatus.toLowerCase();
        if (normalizedStatus.includes("parsing")) return "Reviewing documents";
        if (normalizedStatus.includes("submitted") || normalizedStatus.includes("queued")) return "Queued";
        if (normalizedStatus.includes("in progress")) return "Building assessment";
        if (normalizedStatus.includes("complete")) return "Assessment ready";
        if (normalizedStatus.includes("fail") || normalizedStatus.includes("error")) return "Needs attention";

        return rawStatus
            .replace(/[_-]+/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const formatElapsedTime = (job: any) => {
        const startedAt = job?.startedAt || job?.createdAt || processingDocuments[0]?.uploadedAt;
        const finishedAt = job?.completedAt || job?.updatedAt;

        if (!startedAt) return "--";

        const startMs = new Date(startedAt).getTime();
        const endMs = finishedAt ? new Date(finishedAt).getTime() : Date.now();
        const diffSeconds = Math.max(0, Math.floor((endMs - startMs) / 1000));
        const minutes = Math.floor(diffSeconds / 60);
        const seconds = diffSeconds % 60;

        return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
    };
    const processingSummaryItems = [
        {
            label: "Status",
            value: uploadProgress.status === "uploading"
                ? "Uploading files"
                : isProcessingCompleted
                    ? "Ready"
                    : uploadProgress.status === "failed" && !processingJobDetails
                        ? "Needs attention"
                        : isProcessingFailed
                            ? "Finished with issues"
                            : formatProcessingStatus(processingJobDetails?.jobSubStatus || processingJobDetails?.jobStatus),
        },
        {
            label: "Elapsed Time",
            value: uploadProgress.status === "uploading" && !processingJobDetails
                ? "--"
                : formatElapsedTime(processingJobDetails),
        },
        {
            label: uploadProgress.active ? "Files" : "Documents",
            value: uploadProgress.active
                ? `${uploadProgress.completedFiles}/${uploadProgress.totalFiles}`
                : String(processingDocuments.length),
        },
    ];
    const getElapsedProcessingSeconds = (job: any) => {
        const startedAt = job?.startedAt || job?.createdAt || processingDocuments[0]?.uploadedAt;
        const finishedAt = job?.completedAt || job?.updatedAt;

        if (!startedAt) return 0;

        const startMs = new Date(startedAt).getTime();
        const endMs = finishedAt ? new Date(finishedAt).getTime() : Date.now();
        return Math.max(0, Math.floor((endMs - startMs) / 1000));
    };
    const showMarkAsFailedAction = !isProcessingCompleted
        && !isProcessingFailed
        && getElapsedProcessingSeconds(processingJobDetails) > 120;

    const getSectionDisplayName = (section: any) =>
        String(section?.description || NURSE_SECTION_LABELS[String(section?.recordType || "").trim()] || section?.recordType || "Pending section").trim();

    const getAssessmentStatusLabel = (status?: string) => {
        const normalizedStatus = String(status || "draft").trim().toLowerCase();
        return normalizedStatus === "completed" ? "Completed" : "Draft";
    };

    const getAssessmentStatusStyle = (status?: string) => {
        const normalizedStatus = String(status || "draft").trim().toLowerCase();
        if (normalizedStatus === "completed") {
            return {
                background: "#ecfdf3",
                border: "1px solid #bbf7d0",
                color: "#166534",
            };
        }

        return {
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            color: "#c2410c",
        };
    };

    const handleDownloadAssessment = async () => {
        if (!residentId || !latestAssessmentForm?.formId) return;
        setDownloadingAssessment(true);
        try {
            await backendAPIStore.init();
            const response: any = usesProviderAssessmentAutomation
                ? await backendAPIStore.generateProviderAssessmentDocument(residentId, latestAssessmentForm.formId)
                : await backendAPIStore.generateNurseAssessmentDocument(residentId, latestAssessmentForm.formId);

            if (response?.success === false) {
                toast.current?.show({
                    severity: "warn",
                    summary: "Assessment not ready",
                    detail: response?.message || "Please save at least one assessment section before downloading.",
                    life: 4500,
                });
                return;
            }

            const documentUrl = String(response?.documentUrl || "").trim();
            if (!documentUrl) {
                throw new Error(response?.message || "Assessment document could not be generated.");
            }

            openUrlInNewTab(documentUrl);
            toast.current?.show({
                severity: "success",
                summary: "Download started",
                detail: "The assessment Word document is being downloaded.",
                life: 3500,
            });
        } catch (error: any) {
            if (isSubscriptionRequiredError(error)) {
                if (usesProviderAssessmentAutomation) {
                    openProviderNurseCreditDialog(error);
                } else {
                    openAssessmentSubscriptionDialog(error?.message || error?.details?.error);
                }
                return;
            }
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Failed to download assessment.",
                life: 4000,
            });
        } finally {
            setDownloadingAssessment(false);
        }
    };

    const handleOpenNcpVersion = (targetFormId?: string) => {
        const normalizedFormId = String(targetFormId || "").trim();
        setShowNcpHistoryDialog(false);
        if (!normalizedFormId) {
            navigate(ncpPath);
            return;
        }
        navigate(`${ncpPath}?formId=${encodeURIComponent(normalizedFormId)}`);
    };

    const handleOpenOrCreateNcp = async () => {
        if (!residentId) return;
        if (ncpFormsCount > 0) {
            navigate(ncpPath);
            return;
        }

        setCreatingNcpVersion(true);
        try {
            await backendAPIStore.init();
            const response: any = await backendAPIStore.createNurseNcpForm(residentId);
            const createdFormId = String(response?.formId || "").trim();
            await loadResidentWorkflowSummary();
            navigate(createdFormId ? `${ncpPath}?formId=${encodeURIComponent(createdFormId)}` : ncpPath);
        } catch (error: any) {
            if (isSubscriptionRequiredError(error)) {
                if (isProviderNurseResidentMode) {
                    openProviderNurseCreditDialog(error);
                } else {
                    openAssessmentSubscriptionDialog(error?.message || error?.details?.error);
                }
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: getErrorTitle(error),
                    detail: error?.message || "Unable to start the care plan.",
                    life: 4500,
                });
            }
        } finally {
            setCreatingNcpVersion(false);
        }
    };

    const handleDownloadNcp = async (targetFormId?: string) => {
        const normalizedFormId = String(targetFormId || latestNcpForm?.formId || "").trim();
        if (!residentId || !normalizedFormId) return;

        setDownloadingNcpFormId(normalizedFormId);
        try {
            await backendAPIStore.init();
            const response: any = await backendAPIStore.generateNurseNcpDocument(residentId, normalizedFormId);
            const documentUrl = String(response?.documentUrl || "").trim();

            if (!documentUrl) {
                throw new Error(response?.message || "Care plan document could not be generated.");
            }

            openUrlInNewTab(documentUrl);
            toast.current?.show({
                severity: "success",
                summary: "Download started",
                detail: response?.fileName || "The care plan document is opening in a new tab.",
                life: 3500,
            });
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to download the care plan document.",
                life: 4500,
            });
        } finally {
            setDownloadingNcpFormId("");
        }
    };

    const handleCloseProcessingDialog = useCallback(() => {
        setShowProcessingDialog(false);
        setUploadProgress(DEFAULT_UPLOAD_PROGRESS);

        if (!isProcessingCompleted && !isProcessingFailed) return;

        const targetFormId = String(
            processingJobDetails?.formId
            || latestAssessmentForm?.formId
            || ""
        ).trim();

        if (targetFormId) {
            navigate(`${assessmentPath}?formId=${encodeURIComponent(targetFormId)}`);
            return;
        }

        navigate(assessmentPath);
    }, [assessmentPath, isProcessingCompleted, isProcessingFailed, latestAssessmentForm?.formId, navigate, processingJobDetails?.formId]);

    const formatAgeInYearsOnly = useCallback((value?: string | null) => {
        const rawValue = String(value || "").trim();
        if (!rawValue) {
            return "Not added";
        }
        const matchedYears = rawValue.match(/\d+/);
        return matchedYears ? `${matchedYears[0]} yrs` : rawValue;
    }, []);

    const loadResidentProfileImage = useCallback(async (targetResidentId: string) => {
        try {
            const imageUrl = await backendAPIStore.handlePresignedUrlForDownloadResidentPic(targetResidentId);
            residentProfileImageRefreshAttemptsRef.current.clear();
            setResidentProfileImageUrl(String(imageUrl || "").trim());
        } catch {
            setResidentProfileImageUrl("");
        }
    }, [backendAPIStore]);

    const refreshResidentProfileImageOnError = useCallback((failedUrl?: string) => {
        const normalizedResidentId = String(residentId || "").trim();
        const normalizedFailedUrl = String(failedUrl || residentProfileImageUrl || "").trim();
        if (!normalizedResidentId || !normalizedFailedUrl) {
            return;
        }

        if (residentProfileImageRefreshAttemptsRef.current.has(normalizedFailedUrl)) {
            return;
        }

        residentProfileImageRefreshAttemptsRef.current.add(normalizedFailedUrl);
        void loadResidentProfileImage(normalizedResidentId);
    }, [loadResidentProfileImage, residentId, residentProfileImageUrl]);

    useEffect(() => {
        if (!residentId) {
            setResidentProfileImageUrl("");
            return;
        }
        void loadResidentProfileImage(residentId);
    }, [loadResidentProfileImage, residentId]);

    const sharedResidentDisplayName = useMemo(
        () => [residentProfile?.firstName, residentProfile?.lastName].filter(Boolean).join(" ").trim() || "Resident",
        [residentProfile?.firstName, residentProfile?.lastName],
    );

    const sharedResolvedProfileSnapshot = useMemo(() => ({
        firstName: getFirstNonEmptyString(
            residentProfile?.firstName,
        ),
        lastName: getFirstNonEmptyString(
            residentProfile?.lastName,
        ),
        dateOfBirth: getFirstNonEmptyString(
            carePlanSummaryData?.residentDateOfBirth,
            residentProfile?.dateOfBirth,
        ),
        gender: getFirstNonEmptyString(
            carePlanSummaryData?.residentGender,
            residentProfile?.gender,
        ),
        isOnMedicaid: getFirstDefinedBoolean(
            residentProfile?.isOnMedicaid,
        ),
        anyAllergies: getFirstDefinedBoolean(
            carePlanMedicationManagementData?.anyAllergies,
            carePlanHealthIndicatorData?.anyAllergies,
        ),
        allergiesDescribed: getFirstNonEmptyString(
            carePlanMedicationManagementData?.allergiesDescribed,
            carePlanHealthIndicatorData?.allergySubstanceDetails,
            carePlanSummaryData?.residentAllergies,
            residentProfile?.allergiesDescribed,
        ),
        primaryPhysicianName: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianName,
            carePlanSummaryData?.primaryPhysicianName,
            residentProfile?.primaryPhysicianName,
        ),
        primaryPhysicianClinic: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianClinic,
            carePlanSummaryData?.primaryPhysicianClinic,
            residentProfile?.primaryPhysicianClinic,
        ),
        primaryPhysicianPhoneNumber: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianPhone,
            carePlanResponsiblePartiesData?.primaryPhysicianPhoneNumber,
            carePlanSummaryData?.primaryPhysicianPhone,
            carePlanSummaryData?.primaryPhysicianPhoneNumber,
            residentProfile?.primaryPhysicianPhoneNumber,
        ),
        primaryPhysicianFaxNumber: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianFax,
            carePlanResponsiblePartiesData?.primaryPhysicianFaxNumber,
            carePlanSummaryData?.primaryPhysicianFax,
            carePlanSummaryData?.primaryPhysicianFaxNumber,
            residentProfile?.primaryPhysicianFaxNumber,
        ),
        primaryPhysicianEmail: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianEmail,
            carePlanSummaryData?.primaryPhysicianEmail,
            residentProfile?.primaryPhysicianEmail,
        ),
        primaryPhysicianAddress: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.primaryPhysicianAddress,
            carePlanSummaryData?.primaryPhysicianAddress,
            residentProfile?.primaryPhysicianAddress,
        ),
        pharmacyName: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.pharmacyName,
            carePlanSummaryData?.pharmacyName,
            residentProfile?.pharmacyName,
        ),
        pharmacyPhoneNumber: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.pharmacyPhone,
            carePlanResponsiblePartiesData?.pharmacyPhoneNumber,
            carePlanSummaryData?.pharmacyPhone,
            carePlanSummaryData?.pharmacyPhoneNumber,
            residentProfile?.pharmacyPhoneNumber,
        ),
        pharmacyFaxNumber: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.pharmacyFax,
            carePlanResponsiblePartiesData?.pharmacyFaxNumber,
            carePlanSummaryData?.pharmacyFax,
            carePlanSummaryData?.pharmacyFaxNumber,
            residentProfile?.pharmacyFaxNumber,
        ),
        pharmacyEmail: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.pharmacyEmail,
            carePlanSummaryData?.pharmacyEmail,
            residentProfile?.pharmacyEmail,
        ),
        pharmacyAddress: getFirstNonEmptyString(
            carePlanResponsiblePartiesData?.pharmacyAddress,
            carePlanSummaryData?.pharmacyAddress,
            residentProfile?.pharmacyAddress,
        ),
    }), [
        carePlanHealthIndicatorData?.allergySubstanceDetails,
        carePlanHealthIndicatorData?.anyAllergies,
        carePlanMedicationManagementData?.allergiesDescribed,
        carePlanMedicationManagementData?.anyAllergies,
        carePlanResponsiblePartiesData?.pharmacyAddress,
        carePlanResponsiblePartiesData?.pharmacyEmail,
        carePlanResponsiblePartiesData?.pharmacyFax,
        carePlanResponsiblePartiesData?.pharmacyFaxNumber,
        carePlanResponsiblePartiesData?.pharmacyName,
        carePlanResponsiblePartiesData?.pharmacyPhone,
        carePlanResponsiblePartiesData?.pharmacyPhoneNumber,
        carePlanResponsiblePartiesData?.primaryPhysicianAddress,
        carePlanResponsiblePartiesData?.primaryPhysicianClinic,
        carePlanResponsiblePartiesData?.primaryPhysicianEmail,
        carePlanResponsiblePartiesData?.primaryPhysicianFax,
        carePlanResponsiblePartiesData?.primaryPhysicianFaxNumber,
        carePlanResponsiblePartiesData?.primaryPhysicianName,
        carePlanResponsiblePartiesData?.primaryPhysicianPhone,
        carePlanResponsiblePartiesData?.primaryPhysicianPhoneNumber,
        carePlanSummaryData?.pharmacyAddress,
        carePlanSummaryData?.pharmacyEmail,
        carePlanSummaryData?.pharmacyFax,
        carePlanSummaryData?.pharmacyFaxNumber,
        carePlanSummaryData?.pharmacyName,
        carePlanSummaryData?.pharmacyPhone,
        carePlanSummaryData?.pharmacyPhoneNumber,
        carePlanSummaryData?.primaryPhysicianAddress,
        carePlanSummaryData?.primaryPhysicianClinic,
        carePlanSummaryData?.primaryPhysicianEmail,
        carePlanSummaryData?.primaryPhysicianFax,
        carePlanSummaryData?.primaryPhysicianFaxNumber,
        carePlanSummaryData?.primaryPhysicianName,
        carePlanSummaryData?.primaryPhysicianPhone,
        carePlanSummaryData?.primaryPhysicianPhoneNumber,
        carePlanSummaryData?.residentAllergies,
        carePlanSummaryData?.residentDateOfBirth,
        carePlanSummaryData?.residentGender,
        residentProfile?.allergiesDescribed,
        residentProfile?.dateOfBirth,
        residentProfile?.firstName,
        residentProfile?.gender,
        residentProfile?.isOnMedicaid,
        residentProfile?.lastName,
        residentProfile?.pharmacyAddress,
        residentProfile?.pharmacyEmail,
        residentProfile?.pharmacyFaxNumber,
        residentProfile?.pharmacyName,
        residentProfile?.pharmacyPhoneNumber,
        residentProfile?.primaryPhysicianAddress,
        residentProfile?.primaryPhysicianClinic,
        residentProfile?.primaryPhysicianEmail,
        residentProfile?.primaryPhysicianFaxNumber,
        residentProfile?.primaryPhysicianName,
        residentProfile?.primaryPhysicianPhoneNumber,
    ]);

    const sharedResidentOverviewFacts = useMemo(() => ([
        {
            label: "Age",
            value: sharedResolvedProfileSnapshot.dateOfBirth ? formatAgeInYearsOnly(calculateDeepAge(sharedResolvedProfileSnapshot.dateOfBirth)) : "Not added",
        },
        {
            label: "Date of Birth",
            value: sharedResolvedProfileSnapshot.dateOfBirth || "Not added",
        },
        {
            label: "Sex",
            value: sharedResolvedProfileSnapshot.gender ? capitalizeFirstLetter(String(sharedResolvedProfileSnapshot.gender || "").trim()) : "Not added",
        },
        {
            label: "Medicaid",
            value: typeof sharedResolvedProfileSnapshot.isOnMedicaid === "boolean" ? (sharedResolvedProfileSnapshot.isOnMedicaid ? "Yes" : "No") : "Not added",
        },
        {
            label: "Entry Status",
            value: String(residentProfile?.residentEntryStatus || "in").trim().toLowerCase() === "out" ? "Out" : "In",
        },
    ]), [formatAgeInYearsOnly, residentProfile?.residentEntryStatus, sharedResolvedProfileSnapshot.dateOfBirth, sharedResolvedProfileSnapshot.gender, sharedResolvedProfileSnapshot.isOnMedicaid]);

    const sharedResidentContacts = useMemo(() => (
        Array.isArray(carePlanResponsiblePartiesData?.collateralContacts)
            ? carePlanResponsiblePartiesData.collateralContacts
            : []
    ), [carePlanResponsiblePartiesData?.collateralContacts]);

    const renderProfileDialogError = useCallback((field: string) => {
        const message = profileFormErrors[field];
        if (!message) {
            return null;
        }
        return (
            <small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>
                {message}
            </small>
        );
    }, [profileFormErrors]);

    const openCarePlanContactsDialog = useCallback(() => {
        setCarePlanContactsFormData(buildCarePlanContactsFormState());
        setCarePlanContactsErrors({});
        setIsEditingCarePlanContacts(true);
    }, [buildCarePlanContactsFormState]);

    const openCarePlanBasicsDialog = useCallback(() => {
        setCarePlanBasicsFormData(buildCarePlanBasicsFormState());
        setCarePlanBasicsErrors({});
        setIsEditingCarePlanBasics(true);
    }, [buildCarePlanBasicsFormState]);

    const handleOpenSharedCarePlan = useCallback(() => {
        navigate(ncpPath);
    }, [navigate, ncpPath]);

    const handleGenerateSharedFaceSheet = useCallback(async () => {
        if (!residentId || !homeId) {
            return;
        }
        setGeneratingFaceSheet(true);
        try {
            await backendAPIStore.init();
            const response = await backendAPIStore.generateResidentProfileReportDocument(residentId, homeId);
            const documentUrl = String(response?.documentUrl || "").trim();
            if (!documentUrl) {
                throw new Error(response?.message || "Resident Face Sheet could not be generated.");
            }
            openUrlInNewTab(documentUrl);
            toast.current?.show({
                severity: "success",
                summary: "Download started",
                detail: response?.fileName || "The resident face sheet is opening in a new tab.",
                life: 3500,
            });
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to generate the resident face sheet.",
                life: 4000,
            });
        } finally {
            setGeneratingFaceSheet(false);
        }
    }, [backendAPIStore, homeId, residentId]);

    const saveCarePlanContactsFromProfile = useCallback(async () => {
        if (!residentId || !homeId) {
            return;
        }

        const { normalizedContacts, errors: nextContactErrors } = validateCarePlanContactsForm(carePlanContactsFormData);
        if (Object.keys(nextContactErrors).length > 0) {
            setCarePlanContactsErrors(nextContactErrors);
            toast.current?.show({
                severity: "warn",
                summary: "Complete required fields",
                detail: "Fix highlighted contact fields before saving.",
                life: 3500,
            });
            return;
        }

        setCarePlanContactsErrors({});
        setSavingCarePlanContacts(true);
        try {
            await backendAPIStore.init();
            await backendAPIStore.updateResidentCarePlan({
                ...(carePlanResponsiblePartiesData || { carePlanType: RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES }),
                residentId,
                homeId,
                primaryPhysicianName: String(carePlanContactsFormData?.primaryPhysicianName || "").trim(),
                primaryPhysicianClinic: String(carePlanContactsFormData?.primaryPhysicianClinic || "").trim(),
                primaryPhysicianPhone: normalizedContacts.primaryPhysicianPhone,
                primaryPhysicianPhoneNumber: normalizedContacts.primaryPhysicianPhone,
                primaryPhysicianFax: normalizedContacts.primaryPhysicianFax,
                primaryPhysicianFaxNumber: normalizedContacts.primaryPhysicianFax,
                primaryPhysicianEmail: normalizedContacts.primaryPhysicianEmail,
                primaryPhysicianAddress: String(carePlanContactsFormData?.primaryPhysicianAddress || "").trim(),
                pharmacyName: String(carePlanContactsFormData?.pharmacyName || "").trim(),
                pharmacyPhone: normalizedContacts.pharmacyPhone,
                pharmacyPhoneNumber: normalizedContacts.pharmacyPhone,
                pharmacyFax: normalizedContacts.pharmacyFax,
                pharmacyFaxNumber: normalizedContacts.pharmacyFax,
                pharmacyEmail: normalizedContacts.pharmacyEmail,
                pharmacyAddress: String(carePlanContactsFormData?.pharmacyAddress || "").trim(),
                pharmacyPreferredContactMethod: String(carePlanResponsiblePartiesData?.pharmacyPreferredContactMethod || "").trim(),
                collateralContacts: Array.isArray(carePlanContactsFormData?.collateralContacts)
                    ? carePlanContactsFormData.collateralContacts.map((contact: any) => ({
                        fullName: String(contact?.fullName || "").trim(),
                        relationship: String(contact?.relationship || "").trim(),
                        preferredContactMethod: String(contact?.preferredContactMethod || "").trim(),
                        phoneNumber: String(contact?.phoneNumber || "").trim(),
                        email: String(contact?.email || "").trim(),
                    })).filter((contact: any) =>
                        contact.fullName
                        || contact.relationship
                        || contact.preferredContactMethod
                        || contact.phoneNumber
                        || contact.email,
                    )
                    : [],
            });
            await loadResidentProfile();
            setIsEditingCarePlanContacts(false);
            toast.current?.show({
                severity: "success",
                summary: "Care plan updated",
                detail: "Primary physician, pharmacy, and point of contact details were saved.",
                life: 3200,
            });
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to save contact details.",
                life: 4200,
            });
        } finally {
            setSavingCarePlanContacts(false);
        }
    }, [backendAPIStore, carePlanContactsFormData, carePlanResponsiblePartiesData, homeId, loadResidentProfile, residentId]);

    const saveCarePlanBasicsFromProfile = useCallback(async () => {
        if (!residentId || !homeId) {
            return;
        }

        const hasAllergies = !!carePlanBasicsFormData?.anyAllergies;
        const allergyDetails = String(carePlanBasicsFormData?.allergiesDescribed || "").trim();
        if (hasAllergies && !allergyDetails) {
            setCarePlanBasicsErrors({ allergiesDescribed: "Allergies described is required when Any Allergies is Yes." });
            toast.current?.show({
                severity: "warn",
                summary: "Complete required fields",
                detail: "Add allergies details before saving.",
                life: 3500,
            });
            return;
        }

        setCarePlanBasicsErrors({});
        setSavingCarePlanBasics(true);
        try {
            await backendAPIStore.init();
            await backendAPIStore.updateResidentCarePlan({
                ...(carePlanMedicationManagementData || { carePlanType: RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT }),
                residentId,
                homeId,
                anyAllergies: hasAllergies,
                allergiesDescribed: hasAllergies ? allergyDetails : "",
            });
            await loadResidentProfile();
            setIsEditingCarePlanBasics(false);
            toast.current?.show({
                severity: "success",
                summary: "Care plan updated",
                detail: "Allergy details were saved.",
                life: 3200,
            });
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to save allergy details.",
                life: 4200,
            });
        } finally {
            setSavingCarePlanBasics(false);
        }
    }, [backendAPIStore, carePlanBasicsFormData, carePlanMedicationManagementData, homeId, loadResidentProfile, residentId]);

    const handleMarkProcessingFailed = async () => {
        const activeJobId = String(processingJobDetails?.jobId || "").trim();
        if (!activeJobId) return;

        setAutomationSubmitting(true);
        try {
            await backendAPIStore.init();
            await backendAPIStore.putJobDetails(activeJobId, {
                markFailed: true,
                errorMessage: "Job was marked as failed manually from the resident profile.",
            });
            const failedAt = new Date().toISOString();
            setProcessingJobDetails((prev: any) => ({
                ...(prev || {}),
                jobId: activeJobId,
                jobStatus: JOB_STATUS.FAILED,
                jobSubStatus: JOB_STATUS.FAILED,
                completedAt: failedAt,
                updatedAt: failedAt,
                errorMessage: "Job was marked as failed manually from the resident profile.",
            }));
            setAutomationDocuments((prev) =>
                prev.map((doc: any) =>
                    doc.jobId === activeJobId
                        ? { ...doc, jobStatus: JOB_STATUS.FAILED }
                        : doc
                )
            );
            toast.current?.show({
                severity: "warn",
                summary: "Job marked as failed",
                detail: "The stuck drafting job has been marked as failed.",
                life: 3500,
            });
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to mark the job as failed.",
                life: 4000,
            });
        } finally {
            setAutomationSubmitting(false);
        }
    };

    if (userRoleType !== ROLE_TYPE_NURSE && userRoleType !== ROLE_TYPE_PROVIDER && userRoleType !== ROLE_TYPE_MHP && !isAdminUser) {
        return <Unauthorized />;
    }

    if (isSharedResidentMode) {
        return (
            <div style={{ padding: "1.25rem" }} className="resident-details-page p-component">
                <Toast ref={toast} />
                {loadingProfile ? (
                    <div className="col-12 text-center d-flex justify-content-center align-items-center">
                        <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <div style={{ ...compactProfilePanelStyle, display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.8rem", flexWrap: "wrap" }}>
                            <div>
                                <div style={{ fontWeight: 700, color: "#0f172a" }}>Profile</div>
                                <div style={appMutedTextStyle}>Shared from the resident record.</div>
                            </div>
                            <div className="flex flex-wrap justify-content-end gap-2">
                                <Button
                                    type="button"
                                    label={generatingFaceSheet ? "Generating..." : "Generate Face Sheet"}
                                    icon="pi pi-file-word"
                                    severity="secondary"
                                    rounded
                                    outlined
                                    loading={generatingFaceSheet}
                                    disabled={!residentId || !homeId || generatingFaceSheet}
                                    onClick={() => { void handleGenerateSharedFaceSheet(); }}
                                />
                                {isSharedResidentMode && residentProfile ? (
                                    <Button
                                        type="button"
                                        label={residentProfile.active === false ? "Unarchive" : "Archive Resident"}
                                        icon={residentProfile.active === false ? "pi pi-refresh" : "pi pi-archive"}
                                        severity={residentProfile.active === false ? "success" : "danger"}
                                        rounded
                                        outlined
                                        loading={archivingResident}
                                        disabled={archivingResident}
                                        onClick={confirmSharedResidentArchive}
                                    />
                                ) : null}
                            </div>
                        </div>

                        <div className="grid">
                            <div className="col-12 lg:col-6">
                                <div className="resident-feature-card" style={compactProfileCardStyle}>
                                    <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div>
                                            <div className="flex align-items-center gap-2">
                                                <span className="resident-feature-icon pi pi-id-card" />
                                                <span className="resident-feature-badge resident-feature-badge-primary">Resident Profile</span>
                                                <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Resident Profile</span>
                                            </div>
                                            <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Resident Details</div>
                                        </div>
                                        <Button
                                            type="button"
                                            label="Edit"
                                            icon="pi pi-pencil"
                                            severity="warning"
                                            rounded
                                            outlined
                                            onClick={() => {
                                                setProfileForm(buildSharedResidentProfileFormState(residentProfile));
                                                setProfileFormErrors({});
                                                setProfileError(null);
                                                setEditDialogOpen(true);
                                            }}
                                        />
                                    </div>
                                    <div className="mt-3 flex flex-column align-items-center text-center" style={{ gap: "1rem" }}>
                                        <div className="flex justify-content-center">
                                            {residentProfileImageUrl ? (
                                                <img
                                                    src={residentProfileImageUrl}
                                                    alt={`${sharedResidentDisplayName} profile`}
                                                    width="128"
                                                    height="128"
                                                    onError={(event) => {
                                                        refreshResidentProfileImageOnError(String((event.currentTarget as HTMLImageElement)?.src || residentProfileImageUrl));
                                                    }}
                                                    style={{
                                                        objectFit: "cover",
                                                        border: "3px solid #f2c265",
                                                        borderRadius: "18px",
                                                        display: "block",
                                                        boxShadow: "0 10px 24px rgba(24, 93, 162, 0.12)",
                                                        width: "128px",
                                                        height: "128px",
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="flex align-items-center justify-content-center"
                                                    style={{
                                                        width: "128px",
                                                        height: "128px",
                                                        borderRadius: "18px",
                                                        background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
                                                        border: "2px solid #fde68a",
                                                        color: "#9a3412",
                                                        fontWeight: 700,
                                                        fontSize: "1.75rem",
                                                    }}
                                                >
                                                    {sharedResidentDisplayName.charAt(0) || "R"}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ width: "100%", maxWidth: "520px", display: "flex", flexDirection: "column", gap: "0.75rem", alignItems: "center" }}>
                                            <div>
                                                <div style={{ color: "#0f172a", fontSize: "1.05rem", fontWeight: 800, lineHeight: 1.3 }}>
                                                    {sharedResidentDisplayName}
                                                </div>
                                                <div className="mt-1" style={{ color: residentProfile?.active === false ? "#b91c1c" : "#15803d", fontWeight: 700, fontSize: "0.88rem" }}>
                                                    {residentProfile?.active === false ? "Inactive" : "Active"}
                                                </div>
                                            </div>
                                            <div className="grid justify-content-center" style={compactProfileGridStyle}>
                                                {sharedResidentOverviewFacts.map((item) => (
                                                    <div key={item.label} className="col-12 md:col-6">
                                                        <div style={{ ...compactProfileFieldCardStyle, textAlign: "center" as const }}>
                                                            <div style={compactProfileLabelStyle}>{item.label}</div>
                                                            <div style={compactProfileValueStyle}>{item.value}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 lg:col-6">
                                <div className="resident-feature-card" style={compactProfileCardStyle}>
                                    <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div>
                                            <div className="flex align-items-center gap-2">
                                                <span className="resident-feature-icon pi pi-shield" />
                                                <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Resident Profile</span>
                                            </div>
                                            <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Insurance</div>
                                        </div>
                                        <Button
                                            type="button"
                                            label="Edit"
                                            icon="pi pi-pencil"
                                            severity="warning"
                                            rounded
                                            outlined
                                            onClick={() => {
                                                setProfileForm(buildSharedResidentProfileFormState(residentProfile));
                                                setProfileFormErrors({});
                                                setProfileError(null);
                                                setEditDialogOpen(true);
                                            }}
                                        />
                                    </div>
                                    <div className="grid mt-2" style={compactProfileGridStyle}>
                                        {[
                                            { label: "Medicaid", value: residentProfile?.isOnMedicaid ? "Yes" : "No", col: "col-12 md:col-4" },
                                            { label: "Medicaid Client ID", value: String(residentProfile?.medicaidClientId || "").trim() || "Not added", col: "col-12 md:col-4" },
                                            { label: "ProviderOne", value: String(residentProfile?.providerOneNumber || "").trim() || "Not added", col: "col-12 md:col-4" },
                                            { label: "Primary Insurance", value: String(residentProfile?.primaryInsuranceName || "").trim() || "Not added", col: "col-12 md:col-6" },
                                            { label: "Secondary Insurance", value: String(residentProfile?.secondaryInsuranceName || "").trim() || "Not added", col: "col-12 md:col-6" },
                                            {
                                                label: "Primary Details",
                                                value: [
                                                    residentProfile?.primaryInsurancePlanType ? `Plan: ${residentProfile.primaryInsurancePlanType}` : "",
                                                    residentProfile?.primaryInsuranceMemberId ? `Member ID: ${residentProfile.primaryInsuranceMemberId}` : "",
                                                    residentProfile?.primaryInsuranceGroupNumber ? `Group: ${residentProfile.primaryInsuranceGroupNumber}` : "",
                                                    residentProfile?.primaryInsuranceSubscriberName ? `Subscriber: ${residentProfile.primaryInsuranceSubscriberName}` : "",
                                                    residentProfile?.primaryInsuranceSubscriberRelationship ? `Relationship: ${residentProfile.primaryInsuranceSubscriberRelationship}` : "",
                                                    residentProfile?.primaryInsurancePhone ? `Phone: ${residentProfile.primaryInsurancePhone}` : "",
                                                    residentProfile?.primaryInsuranceClaimsPhone ? `Claims: ${residentProfile.primaryInsuranceClaimsPhone}` : "",
                                                ].filter(Boolean).join(" • ") || "Add carrier, member ID, subscriber, and phone details.",
                                                col: "col-12",
                                            },
                                            {
                                                label: "Secondary Details",
                                                value: [
                                                    residentProfile?.secondaryInsurancePlanType ? `Plan: ${residentProfile.secondaryInsurancePlanType}` : "",
                                                    residentProfile?.secondaryInsuranceMemberId ? `Member ID: ${residentProfile.secondaryInsuranceMemberId}` : "",
                                                    residentProfile?.secondaryInsuranceGroupNumber ? `Group: ${residentProfile.secondaryInsuranceGroupNumber}` : "",
                                                    residentProfile?.secondaryInsuranceSubscriberName ? `Subscriber: ${residentProfile.secondaryInsuranceSubscriberName}` : "",
                                                    residentProfile?.secondaryInsuranceSubscriberRelationship ? `Relationship: ${residentProfile.secondaryInsuranceSubscriberRelationship}` : "",
                                                    residentProfile?.secondaryInsurancePhone ? `Phone: ${residentProfile.secondaryInsurancePhone}` : "",
                                                    residentProfile?.secondaryInsuranceClaimsPhone ? `Claims: ${residentProfile.secondaryInsuranceClaimsPhone}` : "",
                                                ].filter(Boolean).join(" • ") || "Secondary insurance not added.",
                                                col: "col-12",
                                            },
                                            { label: "Notes", value: String(residentProfile?.insuranceNotes || "").trim() || "Not added", col: "col-12" },
                                        ].map((item) => (
                                            <div key={`shared-insurance-card-${item.label}`} className={item.col}>
                                                <div style={compactProfileFieldCardStyle}>
                                                    <div style={compactProfileLabelStyle}>{item.label}</div>
                                                    <div style={{ ...compactProfileValueStyle, whiteSpace: item.col === "col-12" ? "pre-wrap" : "normal" }}>{item.value}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 lg:col-6">
                                <div className="resident-feature-card" style={compactProfileCardStyle}>
                                    <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div>
                                            <div className="flex align-items-center gap-2">
                                                <span className="resident-feature-icon pi pi-exclamation-triangle" />
                                                <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Resident Profile</span>
                                            </div>
                                            <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Allergies</div>
                                        </div>
                                        <Button
                                            type="button"
                                            label="Edit"
                                            icon="pi pi-pencil"
                                            severity="warning"
                                            rounded
                                            outlined
                                            onClick={openCarePlanBasicsDialog}
                                        />
                                    </div>
                                    <div className="grid mt-2" style={compactProfileGridStyle}>
                                        {[
                                            { label: "Any Allergies", value: typeof sharedResolvedProfileSnapshot.anyAllergies === "boolean" ? (sharedResolvedProfileSnapshot.anyAllergies ? "Yes" : "No") : "Not added", col: "col-12" },
                                            { label: "Allergies Described", value: String(sharedResolvedProfileSnapshot.allergiesDescribed || "").trim() || "Not added", col: "col-12" },
                                        ].map((item) => (
                                            <div key={item.label} className={item.col}>
                                                <div style={compactProfileFieldCardStyle}>
                                                    <div style={compactProfileLabelStyle}>{item.label}</div>
                                                    <div style={{ ...compactProfileValueStyle, whiteSpace: item.col === "col-12" ? "pre-wrap" : "normal" }}>{item.value}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 lg:col-6">
                                <div className="resident-feature-card" style={compactProfileCardStyle}>
                                    <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div>
                                            <div className="flex align-items-center gap-2">
                                                <span className="resident-feature-icon pi pi-heart-fill" />
                                                <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Resident Profile</span>
                                            </div>
                                            <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Primary Physician</div>
                                        </div>
                                        <Button
                                            type="button"
                                            label="Edit"
                                            icon="pi pi-pencil"
                                            severity="warning"
                                            rounded
                                            outlined
                                            onClick={openCarePlanContactsDialog}
                                        />
                                    </div>
                                    <div className="grid mt-2" style={compactProfileGridStyle}>
                                        {[
                                            { label: "Name", value: String(sharedResolvedProfileSnapshot.primaryPhysicianName || "").trim() || "Not added", col: "col-12" },
                                            { label: "Clinic", value: String(sharedResolvedProfileSnapshot.primaryPhysicianClinic || "").trim() || "Not added", col: "col-12" },
                                            { label: "Phone", value: String(sharedResolvedProfileSnapshot.primaryPhysicianPhoneNumber || "").trim() || "Not added", col: "col-12 md:col-6" },
                                            { label: "Fax", value: String(sharedResolvedProfileSnapshot.primaryPhysicianFaxNumber || "").trim() || "Not added", col: "col-12 md:col-6" },
                                            { label: "Email", value: String(sharedResolvedProfileSnapshot.primaryPhysicianEmail || "").trim() || "Not added", col: "col-12" },
                                            { label: "Address", value: String(sharedResolvedProfileSnapshot.primaryPhysicianAddress || "").trim() || "Not added", col: "col-12" },
                                        ].map((item) => (
                                            <div key={item.label} className={item.col}>
                                                <div style={compactProfileFieldCardStyle}>
                                                    <div style={compactProfileLabelStyle}>{item.label}</div>
                                                    <div style={{ ...compactProfileValueStyle, whiteSpace: item.label === "Address" ? "pre-wrap" : "normal" }}>{item.value}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 lg:col-6">
                                <div className="resident-feature-card" style={compactProfileCardStyle}>
                                    <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div>
                                            <div className="flex align-items-center gap-2">
                                                <span className="resident-feature-icon pi pi-shopping-bag" />
                                                <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Resident Profile</span>
                                            </div>
                                            <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Primary Pharmacy</div>
                                        </div>
                                        <Button
                                            type="button"
                                            label="Edit"
                                            icon="pi pi-pencil"
                                            severity="warning"
                                            rounded
                                            outlined
                                            onClick={openCarePlanContactsDialog}
                                        />
                                    </div>
                                    <div className="grid mt-2" style={compactProfileGridStyle}>
                                        {[
                                            { label: "Name", value: String(sharedResolvedProfileSnapshot.pharmacyName || "").trim() || "Not added", col: "col-12" },
                                            { label: "Phone", value: String(sharedResolvedProfileSnapshot.pharmacyPhoneNumber || "").trim() || "Not added", col: "col-12 md:col-6" },
                                            { label: "Fax", value: String(sharedResolvedProfileSnapshot.pharmacyFaxNumber || "").trim() || "Not added", col: "col-12 md:col-6" },
                                            { label: "Email", value: String(sharedResolvedProfileSnapshot.pharmacyEmail || "").trim() || "Not added", col: "col-12" },
                                            { label: "Address", value: String(sharedResolvedProfileSnapshot.pharmacyAddress || "").trim() || "Not added", col: "col-12" },
                                        ].map((item) => (
                                            <div key={item.label} className={item.col}>
                                                <div style={compactProfileFieldCardStyle}>
                                                    <div style={compactProfileLabelStyle}>{item.label}</div>
                                                    <div style={{ ...compactProfileValueStyle, whiteSpace: item.label === "Address" ? "pre-wrap" : "normal" }}>{item.value}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 lg:col-6">
                                <div className="resident-feature-card" style={compactProfileCardStyle}>
                                    <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div>
                                            <div className="flex align-items-center gap-2">
                                                <span className="resident-feature-icon pi pi-users" />
                                                <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Resident Profile</span>
                                            </div>
                                            <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Point of Contacts</div>
                                        </div>
                                        <Button
                                            type="button"
                                            label="Edit"
                                            icon="pi pi-pencil"
                                            severity="warning"
                                            rounded
                                            outlined
                                            onClick={openCarePlanContactsDialog}
                                        />
                                    </div>
                                    <div className="mt-2">
                                        {sharedResidentContacts.length > 0 ? (
                                            <div className="grid" style={compactProfileGridStyle}>
                                                {sharedResidentContacts.slice(0, 4).map((contact: any, index: number) => (
                                                    <div key={`${contact?.fullName || "contact"}-${index}`} className="col-12">
                                                        <div style={compactProfileFieldCardStyle}>
                                                            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.25rem", lineHeight: 1.3 }}>
                                                                {contact?.fullName || "Unnamed contact"}
                                                            </div>
                                                            <div className="grid" style={compactProfileGridStyle}>
                                                                <div className="col-12 md:col-6"><span style={compactProfileLabelStyle}>Relationship:</span> <span style={compactProfileValueStyle}>{contact?.relationship || "Not added"}</span></div>
                                                                <div className="col-12 md:col-6"><span style={compactProfileLabelStyle}>Preferred:</span> <span style={compactProfileValueStyle}>{contact?.preferredContactMethod || "Not added"}</span></div>
                                                                <div className="col-12 md:col-6"><span style={compactProfileLabelStyle}>Phone:</span> <span style={compactProfileValueStyle}>{contact?.phoneNumber || "Not added"}</span></div>
                                                                <div className="col-12 md:col-6"><span style={compactProfileLabelStyle}>Email:</span> <span style={compactProfileValueStyle}>{contact?.email || "Not added"}</span></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div style={{ color: "#475569" }}>
                                                No care plan contacts added yet.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <Dialog
                    visible={editDialogOpen}
                    modal
                    header={renderSurfaceHeader("Edit Resident", "", "pi pi-user-edit")}
                    footer={editDialogFooter}
                    style={appDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                    onHide={() => {
                        if (!editDialogOpen) return;
                        setEditDialogOpen(false);
                        setProfileError(null);
                    }}
                >
                    <div style={appFormSectionStyle}>
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentFirstName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>First Name</label>
                                <InputText id="sharedResidentFirstName" value={profileForm.firstName} onChange={(e) => setProfileForm((prev) => ({ ...prev, firstName: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("firstName")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentLastName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Last Name</label>
                                <InputText id="sharedResidentLastName" value={profileForm.lastName} onChange={(e) => setProfileForm((prev) => ({ ...prev, lastName: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("lastName")}
                            </div>
                            <div className="col-12">
                                <div className="flex align-items-center gap-2">
                                    <Checkbox inputId="sharedResidentActive" checked={profileForm.active !== false} onChange={(e) => setProfileForm((prev) => ({ ...prev, active: !!e.checked }))} />
                                    <label htmlFor="sharedResidentActive" style={{ color: "#334155", fontWeight: 600 }}>Keep resident active</label>
                                </div>
                                <div className="mt-2" style={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.55 }}>
                                    Insurance details below are stored on the resident profile. Allergies, physician, pharmacy, and point of contacts are maintained from the local care plan dialogs on this page.
                                </div>
                            </div>
                            <div className="col-12"><Divider align="left"><strong>Insurance</strong></Divider></div>
                            <div className="col-12">
                                <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.85rem 1rem", background: "#f8fafc" }}>
                                    <div className="flex align-items-center gap-2 flex-wrap">
                                        <Checkbox inputId="sharedResidentMedicaid" checked={!!profileForm.isOnMedicaid} onChange={(e) => setProfileForm((prev) => ({ ...prev, isOnMedicaid: !!e.checked }))} />
                                        <label htmlFor="sharedResidentMedicaid" style={{ color: "#334155", fontWeight: 600 }}>On Medicaid</label>
                                    </div>
                                    <div className="mt-2" style={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.5 }}>
                                        If Medicaid is enabled, add either the Medicaid Client ID or the ProviderOne Number.
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentMedicaidClientId" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Medicaid Client ID</label>
                                <InputText id="sharedResidentMedicaidClientId" value={profileForm.medicaidClientId} onChange={(e) => setProfileForm((prev) => ({ ...prev, medicaidClientId: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("medicaidClientId")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentProviderOneNumber" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>ProviderOne Number</label>
                                <InputText id="sharedResidentProviderOneNumber" value={profileForm.providerOneNumber} onChange={(e) => setProfileForm((prev) => ({ ...prev, providerOneNumber: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("providerOneNumber")}
                            </div>
                            <div className="col-12"><Divider align="left"><strong>Primary Insurance</strong></Divider></div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Carrier</label>
                                <InputText id="sharedResidentPrimaryInsuranceName" value={profileForm.primaryInsuranceName} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceName: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("primaryInsuranceName")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsurancePlanType" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Plan Type</label>
                                <InputText id="sharedResidentPrimaryInsurancePlanType" value={profileForm.primaryInsurancePlanType} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsurancePlanType: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceMemberId" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Member ID</label>
                                <InputText id="sharedResidentPrimaryInsuranceMemberId" value={profileForm.primaryInsuranceMemberId} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceMemberId: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("primaryInsuranceMemberId")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceGroupNumber" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Group Number</label>
                                <InputText id="sharedResidentPrimaryInsuranceGroupNumber" value={profileForm.primaryInsuranceGroupNumber} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceGroupNumber: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceSubscriberName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Subscriber Name</label>
                                <InputText id="sharedResidentPrimaryInsuranceSubscriberName" value={profileForm.primaryInsuranceSubscriberName} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceSubscriberName: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("primaryInsuranceSubscriberName")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceSubscriberRelationship" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Relationship</label>
                                <InputText id="sharedResidentPrimaryInsuranceSubscriberRelationship" value={profileForm.primaryInsuranceSubscriberRelationship} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceSubscriberRelationship: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsurancePhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Phone</label>
                                <InputText id="sharedResidentPrimaryInsurancePhone" value={profileForm.primaryInsurancePhone} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsurancePhone: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceClaimsPhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Claims Phone</label>
                                <InputText id="sharedResidentPrimaryInsuranceClaimsPhone" value={profileForm.primaryInsuranceClaimsPhone} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceClaimsPhone: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12"><Divider align="left"><strong>Secondary Insurance</strong></Divider></div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Carrier</label>
                                <InputText id="sharedResidentSecondaryInsuranceName" value={profileForm.secondaryInsuranceName} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceName: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("secondaryInsuranceName")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsurancePlanType" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Plan Type</label>
                                <InputText id="sharedResidentSecondaryInsurancePlanType" value={profileForm.secondaryInsurancePlanType} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsurancePlanType: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceMemberId" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Member ID</label>
                                <InputText id="sharedResidentSecondaryInsuranceMemberId" value={profileForm.secondaryInsuranceMemberId} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceMemberId: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("secondaryInsuranceMemberId")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceGroupNumber" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Group Number</label>
                                <InputText id="sharedResidentSecondaryInsuranceGroupNumber" value={profileForm.secondaryInsuranceGroupNumber} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceGroupNumber: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceSubscriberName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Subscriber Name</label>
                                <InputText id="sharedResidentSecondaryInsuranceSubscriberName" value={profileForm.secondaryInsuranceSubscriberName} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceSubscriberName: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceSubscriberRelationship" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Relationship</label>
                                <InputText id="sharedResidentSecondaryInsuranceSubscriberRelationship" value={profileForm.secondaryInsuranceSubscriberRelationship} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceSubscriberRelationship: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsurancePhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Phone</label>
                                <InputText id="sharedResidentSecondaryInsurancePhone" value={profileForm.secondaryInsurancePhone} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsurancePhone: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceClaimsPhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Claims Phone</label>
                                <InputText id="sharedResidentSecondaryInsuranceClaimsPhone" value={profileForm.secondaryInsuranceClaimsPhone} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceClaimsPhone: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12">
                                <label htmlFor="sharedResidentInsuranceNotes" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Insurance Notes</label>
                                <InputTextarea id="sharedResidentInsuranceNotes" value={profileForm.insuranceNotes} onChange={(e) => setProfileForm((prev) => ({ ...prev, insuranceNotes: e.target.value }))} className="w-full" rows={4} autoResize />
                            </div>
                        </div>
                    </div>
                </Dialog>

                <CarePlanContactsDialog
                    header="Edit Care Plan Contacts"
                    visible={isEditingCarePlanContacts}
                    style={appDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                    formData={carePlanContactsFormData}
                    setFormData={setCarePlanContactsFormData}
                    errors={carePlanContactsErrors}
                    saving={savingCarePlanContacts}
                    onSave={saveCarePlanContactsFromProfile}
                    onHide={() => {
                        setCarePlanContactsFormData(buildCarePlanContactsFormState());
                        setCarePlanContactsErrors({});
                        setIsEditingCarePlanContacts(false);
                    }}
                />

                <Dialog
                    header="Edit Allergy Details"
                    visible={isEditingCarePlanBasics}
                    style={appDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                    modal
                    onHide={() => {
                        setCarePlanBasicsFormData(buildCarePlanBasicsFormState());
                        setCarePlanBasicsErrors({});
                        setIsEditingCarePlanBasics(false);
                    }}
                >
                    <div className="grid">
                        <div className="col-12">
                            <div className="flex align-items-center gap-2">
                                <Checkbox
                                    inputId="sharedCarePlanAnyAllergies"
                                    checked={!!carePlanBasicsFormData?.anyAllergies}
                                    onChange={(e) => {
                                        const checked = !!e.checked;
                                        setCarePlanBasicsFormData((prev: any) => ({ ...prev, anyAllergies: checked, allergiesDescribed: checked ? prev.allergiesDescribed : "" }));
                                        setCarePlanBasicsErrors({});
                                    }}
                                />
                                <label htmlFor="sharedCarePlanAnyAllergies" style={{ color: "#334155", fontWeight: 600 }}>Any allergies</label>
                            </div>
                        </div>
                        <div className="col-12">
                            <label htmlFor="sharedCarePlanAllergiesDescribed" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Allergies Described</label>
                            <InputTextarea
                                id="sharedCarePlanAllergiesDescribed"
                                value={carePlanBasicsFormData?.allergiesDescribed || ""}
                                rows={4}
                                className="w-full"
                                disabled={!carePlanBasicsFormData?.anyAllergies}
                                onChange={(e) => {
                                    setCarePlanBasicsFormData((prev: any) => ({ ...prev, allergiesDescribed: e.target.value }));
                                    setCarePlanBasicsErrors({});
                                }}
                            />
                            {carePlanBasicsErrors?.allergiesDescribed ? (
                                <small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>{carePlanBasicsErrors.allergiesDescribed}</small>
                            ) : null}
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap justify-content-end gap-2">
                        <Button type="button" label="Cancel" severity="secondary" rounded outlined onClick={() => {
                            setCarePlanBasicsFormData(buildCarePlanBasicsFormState());
                            setCarePlanBasicsErrors({});
                            setIsEditingCarePlanBasics(false);
                        }} />
                        <Button type="button" label={savingCarePlanBasics ? "Saving..." : "Save to Care Plan"} icon="pi pi-save" severity="warning" rounded onClick={() => { void saveCarePlanBasicsFromProfile(); }} loading={savingCarePlanBasics} disabled={savingCarePlanBasics} />
                    </div>
                </Dialog>
            </div>
        );
    }

    return (
        <div style={{ padding: "1.25rem" }} className="resident-details-page p-component">
            <Toast ref={toast} />
            <style>
                {`
                    .resident-details-page .resident-workspace-tabs .p-tabview-nav-container {
                        margin-bottom: 1rem;
                    }

                    .resident-details-page .resident-workspace-tabs {
                        width: 100%;
                    }

                    .resident-details-page .resident-workspace-tabs .p-tabview-nav {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 0.55rem;
                        padding: 0.45rem;
                        border: 1px solid #dbe4f0;
                        border-radius: 18px;
                        background: linear-gradient(180deg, #f8fbff 0%, #f1f5f9 100%);
                        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
                        width: 100%;
                        max-width: 100%;
                        margin: 0;
                        justify-content: center;
                    }

                    .resident-details-page .resident-workspace-tabs .p-tabview-nav li {
                        margin: 0;
                    }

                    .resident-details-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link {
                        border: 1px solid #d7e0ea;
                        border-radius: 14px;
                        background: #ffffff;
                        color: #475569;
                        padding: 0.8rem 1rem;
                        font-weight: 700;
                        transition: all 0.18s ease;
                        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
                    }

                    .resident-details-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link:not(.p-disabled):focus {
                        box-shadow: 0 0 0 0.18rem rgba(249, 115, 22, 0.16);
                    }

                    .resident-details-page .resident-workspace-tabs .p-tabview-nav li:not(.p-highlight) .p-tabview-nav-link:hover {
                        border-color: #fdba74;
                        background: linear-gradient(180deg, #ffffff 0%, #fff7ed 100%);
                        color: #9a3412;
                        transform: translateY(-1px);
                    }

                    .resident-details-page .resident-workspace-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {
                        border-color: #fdba74;
                        background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 55%, #fef3c7 100%);
                        color: #0f172a;
                        box-shadow: 0 8px 18px rgba(249, 115, 22, 0.14);
                    }

                    .resident-details-page .resident-workspace-tabs .p-tabview-panels {
                        padding: 0;
                        background: transparent;
                    }

                    .resident-details-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel {
                        display: flex;
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .resident-details-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel > * {
                        width: 100%;
                        max-width: none;
                        margin-left: 0;
                        margin-right: 0;
                    }

                `}
            </style>
            <div style={dashboardPageContainerStyle}>
                {renderDashboardHeader(
                    `Resident Profile${residentProfile?.firstName || residentProfile?.lastName ? ` - ${`${residentProfile?.firstName || ""} ${residentProfile?.lastName || ""}`.trim()}` : ""}`,
                    "Manage resident details, care documents, and delegation records in one place.",
                    <div className="flex gap-2 flex-wrap align-items-center justify-content-end">
                        <Button
                            type="button"
                            label="Binder"
                            severity="secondary"
                            rounded
                            outlined
                            icon="pi pi-folder-open"
                            onClick={() => navigate(binderPath)}
                            disabled={loadingProfile || !residentProfile || !canUseSharedBinderWorkspace}
                        />
                    </div>,
                )}

                <div style={{ ...appSectionCardStyle, overflow: "hidden", background: "#ffffff" }}>
                    <div style={{ padding: "1rem 1.15rem" }}>
                        <div className="grid justify-content-center">
                            <div className="col-12 xl:col-10">
                                <div className="grid" style={getSetupWizardHighlightStyle("workflow-cards")}>
                                    <div className="col-12">
                                        <TabView
                                            className="resident-workspace-tabs"
                                            activeIndex={activeWorkflowTabIndex}
                                            onTabChange={(e) => setActiveWorkflowTabIndex(e.index)}
                                            scrollable
                                        >
                                                <TabPanel header="Assessment">
                                                    <div className="grid">
                                                        <div className="col-12 lg:col-7">
                                                            <div className="resident-feature-card" style={getSetupWizardHighlightStyle("assessment-card")}>
                                                                <div className="resident-feature-head">
                                                                    <span className="resident-feature-icon pi pi-file-edit" />
                                                                    <span className="resident-feature-badge resident-feature-badge-primary">Assessment</span>
                                                                </div>
                                                                <div className="resident-feature-title">Assessment</div>
                                                                <div className="resident-feature-copy">
                                                                    Use resident source files such as Face sheet, MAR sheet, clinical records, assessment notes, and audio files to create the draft assessment.
                                                                </div>
                                                                <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                    <div className="flex flex-wrap align-items-center gap-2">
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.96rem" }}>
                                                                            {assessmentFormsCount} version{assessmentFormsCount === 1 ? "" : "s"}
                                                                        </div>
                                                                        {latestAssessmentForm ? (
                                                                            <span style={{
                                                                                ...getAssessmentStatusStyle(latestAssessmentForm?.status),
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                borderRadius: 999,
                                                                                padding: "0.2rem 0.6rem",
                                                                                fontSize: "0.78rem",
                                                                                fontWeight: 700,
                                                                            }}>
                                                                                {getAssessmentStatusLabel(latestAssessmentForm?.status)}
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                    <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem", lineHeight: 1.6 }}>
                                                                        {assessmentFormsCount === 0
                                                                            ? "No assessment has been started yet."
                                                                            : latestAssessmentForm?.version
                                                                                ? `Latest saved version is V${latestAssessmentForm.version}.`
                                                                                : "A saved assessment is ready to review and update."}
                                                                    </div>
                                                                </div>
                                                                <div className="mt-3 flex flex-wrap gap-2">
                                                                    <Button
                                                                        type="button"
                                                                        label={assessmentFormsCount > 0 ? "Open Latest Version" : "Start Assessment"}
                                                                        severity="warning"
                                                                        rounded
                                                                        outlined
                                                                        style={getSetupWizardHighlightStyle("assessment-card")}
                                                                        onClick={() => navigate(
                                                                            latestAssessmentForm?.formId
                                                                                ? `${assessmentPath}?formId=${encodeURIComponent(String(latestAssessmentForm.formId))}`
                                                                                : assessmentPath
                                                                        )}
                                                                    />
                                                                    {assessmentFormsCount > 0 ? (
                                                                        <Button
                                                                            type="button"
                                                                            label="Previous Versions"
                                                                            severity="secondary"
                                                                            rounded
                                                                            outlined
                                                                            onClick={() => setShowAssessmentHistoryDialog(true)}
                                                                        />
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-12 lg:col-5">
                                                            <div className="resident-feature-card">
                                                                <div className="resident-feature-head">
                                                                    <span className="resident-feature-icon pi pi-history" />
                                                                    <span className="resident-feature-badge resident-feature-badge-primary">Latest Version</span>
                                                                </div>
                                                                <div className="resident-feature-title">Assessment Snapshot</div>
                                                                <div className="resident-feature-copy">
                                                                    A quick summary of the current assessment workflow before you open the full assessment page.
                                                                </div>
                                                                <div className="mt-3 flex flex-column gap-2">
                                                                    <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                                                            {latestAssessmentForm?.version
                                                                                ? `Version V${latestAssessmentForm.version}`
                                                                                : assessmentFormsCount > 0
                                                                                    ? "Saved assessment available"
                                                                                : "No assessment selected"}
                                                                        </div>
                                                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                                                            {latestAssessmentForm?.createdAt
                                                                                ? `Created ${new Date(latestAssessmentForm.createdAt).toLocaleDateString()}`
                                                                                : "The latest assessment version will appear here once one exists."}
                                                                        </div>
                                                                    </div>
                                                                    <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                                                            Care planning status
                                                                        </div>
                                                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                                                            {assessmentFormsCount > 0
                                                                                ? "Open the assessment workspace to continue documentation, review versions, and update care-plan-related content."
                                                                                : "Start the first assessment to begin care-planning work for this resident."}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TabPanel>

                                                <TabPanel header="Foot & Nail Care">
                                                    <div className="grid">
                                                        <div className="col-12 lg:col-7">
                                                            <div className="resident-feature-card">
                                                                <div className="resident-feature-head">
                                                                    <span className="resident-feature-icon pi pi-heart" />
                                                                    <span className="resident-feature-badge resident-feature-badge-primary">Foot & Nail Care</span>
                                                                </div>
                                                                <div className="resident-feature-title">Foot & Nail Care</div>
                                                                <div className="resident-feature-copy">
                                                                    Manage the consent form and tracker from the resident&apos;s care plan, then download or add the completed document to binder.
                                                                </div>
                                                                <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.96rem" }}>
                                                                        {footNailCareFormsCount} draft{footNailCareFormsCount === 1 ? "" : "s"}
                                                                    </div>
                                                                    <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem", lineHeight: 1.6 }}>
                                                                        {footNailCareFormsCount === 0
                                                                            ? "No foot and nail care drafts started yet."
                                                                            : footNailCareFormsCount === 1
                                                                                ? "1 foot and nail care draft is already saved for this resident."
                                                                                : `${footNailCareFormsCount} foot and nail care drafts are already saved for this resident.`}
                                                                    </div>
                                                                </div>
                                                                <div className="mt-3 flex flex-wrap gap-2">
                                                                    <Button
                                                                        type="button"
                                                                        label="Open Foot & Nail Care"
                                                                        severity="secondary"
                                                                        rounded
                                                                        outlined
                                                                        onClick={() => navigate(podiatristPath)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-12 lg:col-5">
                                                            <div className="resident-feature-card">
                                                                <div className="resident-feature-head">
                                                                    <span className="resident-feature-icon pi pi-info-circle" />
                                                                    <span className="resident-feature-badge resident-feature-badge-primary">What You Need</span>
                                                                </div>
                                                                <div className="resident-feature-title">Before You Start</div>
                                                                <div className="resident-feature-copy">
                                                                    Keep this workflow simple: care plan first, then generate the forms, review the clinical details, and save the final version to binder.
                                                                </div>
                                                                <div className="mt-3 flex flex-column gap-2">
                                                                    <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                                                            Care Plan
                                                                        </div>
                                                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                                                            Required for diagnosis and allergy details.
                                                                        </div>
                                                                    </div>
                                                                    <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                                                            Two Forms
                                                                        </div>
                                                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                                                            Consent Form and Tracker are both generated together.
                                                                        </div>
                                                                    </div>
                                                                    <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                                                            Final Steps
                                                                        </div>
                                                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                                                            Edit, download, or add to binder when ready.
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TabPanel>

                                                <TabPanel header="Delegation Forms">
                                                    <div className="grid">
                                                        <div className="col-12 lg:col-7">
                                                            <div className="resident-feature-card" style={getSetupWizardHighlightStyle("delegation-card")}>
                                                                <div className="resident-feature-head">
                                                                    <span className="resident-feature-icon pi pi-briefcase" />
                                                                    <span className="resident-feature-badge resident-feature-badge-primary">Delegation Forms</span>
                                                                </div>
                                                                <div className="resident-feature-title">Delegation Forms</div>
                                                                <div className="resident-feature-copy">
                                                                    Create delegation forms using the resident&apos;s Negotiated Care Plan, Assessment, or by starting manually.
                                                                </div>
                                                                <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.96rem" }}>
                                                                        {delegationFormsCount} version{delegationFormsCount === 1 ? "" : "s"}
                                                                    </div>
                                                                    <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem", lineHeight: 1.6 }}>
                                                                        {delegationFormsCount === 0
                                                                            ? "No delegation forms started yet."
                                                                            : delegationFormsCount === 1
                                                                                ? "1 delegation form is already saved for this resident."
                                                                                : `${delegationFormsCount} delegation forms are already saved for this resident.`}
                                                                    </div>
                                                                </div>
                                                                <div className="mt-3 flex flex-wrap gap-2">
                                                                    <Button
                                                                        type="button"
                                                                        label="Open Delegation Forms"
                                                                        severity="secondary"
                                                                        rounded
                                                                        outlined
                                                                        style={getSetupWizardHighlightStyle("delegation-card")}
                                                                        onClick={() => navigate(delegationPath)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-12 lg:col-5">
                                                            <div className="resident-feature-card">
                                                                <div className="resident-feature-head">
                                                                    <span className="resident-feature-icon pi pi-sparkles" />
                                                                    <span className="resident-feature-badge resident-feature-badge-primary">Workflow</span>
                                                                </div>
                                                                <div className="resident-feature-title">Ways To Create</div>
                                                                <div className="resident-feature-copy">
                                                                    Choose the source that fits the resident&apos;s workflow and documentation you already have.
                                                                </div>
                                                                <div className="mt-3 flex flex-column gap-2">
                                                                    <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                                                            Care Plan
                                                                        </div>
                                                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                                                            Start from the resident&apos;s Negotiated Care Plan when that plan already captures the current support needs.
                                                                        </div>
                                                                    </div>
                                                                    <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                                                            Assessment
                                                                        </div>
                                                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                                                            Start from the latest assessment when you want delegation forms based on the resident&apos;s assessment workflow.
                                                                        </div>
                                                                    </div>
                                                                    <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                                                            Create Manually
                                                                        </div>
                                                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                                                            Start a blank delegation form when you already know exactly which form needs to be completed.
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TabPanel>

                                                <TabPanel header="Negotiated Care Plan (DSHS template)">
                                                    <div className="grid">
                                                        <div className="col-12 lg:col-7">
                                                            <div className="resident-feature-card">
                                                                <div className="resident-feature-head">
                                                                    <span className="resident-feature-icon pi pi-directions-alt" />
                                                                    <span className="resident-feature-badge resident-feature-badge-primary">DSHS Template</span>
                                                                </div>
                                                                <div className="resident-feature-title">Negotiated Care Plan (DSHS template)</div>
                                                                <div className="resident-feature-copy">
                                                                    Open the DSHS-template negotiated care plan, review the latest saved version, and download the latest document when available.
                                                                </div>
                                                                <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                    <div className="flex flex-wrap align-items-center gap-2">
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.96rem" }}>
                                                                            {ncpFormsCount} version{ncpFormsCount === 1 ? "" : "s"}
                                                                        </div>
                                                                        {latestNcpForm ? (
                                                                            <span style={{
                                                                                ...getAssessmentStatusStyle(latestNcpForm?.status),
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                borderRadius: 999,
                                                                                padding: "0.2rem 0.6rem",
                                                                                fontSize: "0.78rem",
                                                                                fontWeight: 700,
                                                                            }}>
                                                                                {getAssessmentStatusLabel(latestNcpForm?.status)}
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                    <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem", lineHeight: 1.6 }}>
                                                                        {ncpFormsCount === 0
                                                                            ? "No care plan version has been started yet."
                                                                            : latestNcpForm?.version
                                                                                ? `Latest saved version is V${latestNcpForm.version}.`
                                                                                : "A care plan draft is ready to review."}
                                                                    </div>
                                                                </div>
                                                                <div className="mt-3 flex flex-wrap gap-2">
                                                                    <Button
                                                                        type="button"
                                                                        label={ncpFormsCount > 0 ? "Edit" : "Start New"}
                                                                        severity="secondary"
                                                                        rounded
                                                                        outlined
                                                                        loading={creatingNcpVersion}
                                                                        onClick={() => { void handleOpenOrCreateNcp(); }}
                                                                    />
                                                                    {latestNcpForm ? (
                                                                        <Button
                                                                            type="button"
                                                                            label="Download"
                                                                            severity="secondary"
                                                                            rounded
                                                                            outlined
                                                                            loading={downloadingNcpFormId === String(latestNcpForm?.formId || "")}
                                                                            onClick={() => { void handleDownloadNcp(latestNcpForm?.formId); }}
                                                                        />
                                                                    ) : null}
                                                                    {canGenerateMarFromProfile ? (
                                                                        <Button
                                                                            type="button"
                                                                            label="Generate MAR"
                                                                            severity="warning"
                                                                            rounded
                                                                            outlined
                                                                            onClick={() => navigate(emarPath)}
                                                                        />
                                                                    ) : null}
                                                                    {ncpFormsCount > 0 && !isProviderNurseResidentMode ? (
                                                                        <Button
                                                                            type="button"
                                                                            label="Versions"
                                                                            severity="secondary"
                                                                            rounded
                                                                            outlined
                                                                            onClick={() => setShowNcpHistoryDialog(true)}
                                                                        />
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-12 lg:col-5">
                                                            <div className="resident-feature-card">
                                                                <div className="resident-feature-head">
                                                                    <span className="resident-feature-icon pi pi-history" />
                                                                    <span className="resident-feature-badge resident-feature-badge-primary">Latest Version</span>
                                                                </div>
                                                                <div className="resident-feature-title">Plan Snapshot</div>
                                                                <div className="resident-feature-copy">
                                                                    A quick summary of the DSHS-template care plan before opening the full workspace.
                                                                </div>
                                                                <div className="mt-3 flex flex-column gap-2">
                                                                    <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                                                            {latestNcpForm?.version
                                                                                ? `Version V${latestNcpForm.version}`
                                                                                : ncpFormsCount > 0
                                                                                    ? "Saved negotiated care plan available"
                                                                                    : "No negotiated care plan selected"}
                                                                        </div>
                                                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                                                            {latestNcpForm?.createdAt
                                                                                ? `Created ${new Date(latestNcpForm.createdAt).toLocaleDateString()}`
                                                                                : "The latest negotiated care plan version will appear here once one exists."}
                                                                        </div>
                                                                    </div>
                                                                    <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                                                                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                                                            DSHS template workflow
                                                                        </div>
                                                                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                                                            Use this page when you need the resident&apos;s negotiated care plan in the DSHS template format.
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TabPanel>
                                        </TabView>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Dialog
                    visible={editDialogOpen}
                    modal
                    header={renderSurfaceHeader("Edit Resident", "", "pi pi-user-edit")}
                    footer={editDialogFooter}
                    style={appDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                    onHide={() => {
                        if (!editDialogOpen) return;
                        setEditDialogOpen(false);
                        setProfileError(null);
                    }}
                >
                    <div style={appFormSectionStyle}>
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <label htmlFor="residentFirstName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>First Name</label>
                                <InputText
                                    id="residentFirstName"
                                    value={profileForm.firstName}
                                    onChange={(e) => setProfileForm((prev) => ({ ...prev, firstName: e.target.value }))}
                                    className="w-full"
                                />
                                {renderProfileDialogError("firstName")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="residentLastName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Last Name</label>
                                <InputText
                                    id="residentLastName"
                                    value={profileForm.lastName}
                                    onChange={(e) => setProfileForm((prev) => ({ ...prev, lastName: e.target.value }))}
                                    className="w-full"
                                />
                                {renderProfileDialogError("lastName")}
                            </div>
                            <div className="col-12">
                                <div className="flex align-items-center gap-2">
                                    <Checkbox
                                        inputId="sharedResidentActive"
                                        checked={profileForm.active !== false}
                                        onChange={(e) => setProfileForm((prev) => ({ ...prev, active: !!e.checked }))}
                                    />
                                    <label htmlFor="sharedResidentActive" style={{ color: "#334155", fontWeight: 600 }}>Keep resident active</label>
                                </div>
                                <div className="mt-2" style={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.55 }}>
                                    Core care plan details like physician, pharmacy, allergies, and contacts stay managed from the care plan workspace. Insurance details here are stored on the resident profile.
                                </div>
                            </div>
                            <div className="col-12"><Divider align="left"><strong>Insurance</strong></Divider></div>
                            <div className="col-12">
                                <div
                                    style={{
                                        border: "1px solid #e2e8f0",
                                        borderRadius: "12px",
                                        padding: "0.85rem 1rem",
                                        background: "#f8fafc",
                                    }}
                                >
                                    <div className="flex align-items-center gap-2 flex-wrap">
                                        <Checkbox
                                            inputId="sharedResidentMedicaid"
                                            checked={!!profileForm.isOnMedicaid}
                                            onChange={(e) => setProfileForm((prev) => ({ ...prev, isOnMedicaid: !!e.checked }))}
                                        />
                                        <label htmlFor="sharedResidentMedicaid" style={{ color: "#334155", fontWeight: 600 }}>On Medicaid</label>
                                    </div>
                                    <div className="mt-2" style={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.5 }}>
                                        If Medicaid is enabled, add either the Medicaid Client ID or the ProviderOne Number.
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentMedicaidClientId" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Medicaid Client ID</label>
                                <InputText
                                    id="sharedResidentMedicaidClientId"
                                    value={profileForm.medicaidClientId}
                                    onChange={(e) => setProfileForm((prev) => ({ ...prev, medicaidClientId: e.target.value }))}
                                    className="w-full"
                                />
                                {renderProfileDialogError("medicaidClientId")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentProviderOneNumber" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>ProviderOne Number</label>
                                <InputText
                                    id="sharedResidentProviderOneNumber"
                                    value={profileForm.providerOneNumber}
                                    onChange={(e) => setProfileForm((prev) => ({ ...prev, providerOneNumber: e.target.value }))}
                                    className="w-full"
                                />
                                {renderProfileDialogError("providerOneNumber")}
                            </div>
                            <div className="col-12"><Divider align="left"><strong>Primary Insurance</strong></Divider></div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Carrier</label>
                                <InputText id="sharedResidentPrimaryInsuranceName" value={profileForm.primaryInsuranceName} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceName: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("primaryInsuranceName")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsurancePlanType" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Plan Type</label>
                                <InputText id="sharedResidentPrimaryInsurancePlanType" value={profileForm.primaryInsurancePlanType} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsurancePlanType: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceMemberId" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Member ID</label>
                                <InputText id="sharedResidentPrimaryInsuranceMemberId" value={profileForm.primaryInsuranceMemberId} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceMemberId: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("primaryInsuranceMemberId")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceGroupNumber" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Group Number</label>
                                <InputText id="sharedResidentPrimaryInsuranceGroupNumber" value={profileForm.primaryInsuranceGroupNumber} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceGroupNumber: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceSubscriberName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Subscriber Name</label>
                                <InputText id="sharedResidentPrimaryInsuranceSubscriberName" value={profileForm.primaryInsuranceSubscriberName} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceSubscriberName: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("primaryInsuranceSubscriberName")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceSubscriberRelationship" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Relationship</label>
                                <InputText id="sharedResidentPrimaryInsuranceSubscriberRelationship" value={profileForm.primaryInsuranceSubscriberRelationship} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceSubscriberRelationship: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsurancePhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Phone</label>
                                <InputText id="sharedResidentPrimaryInsurancePhone" value={profileForm.primaryInsurancePhone} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsurancePhone: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentPrimaryInsuranceClaimsPhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Claims Phone</label>
                                <InputText id="sharedResidentPrimaryInsuranceClaimsPhone" value={profileForm.primaryInsuranceClaimsPhone} onChange={(e) => setProfileForm((prev) => ({ ...prev, primaryInsuranceClaimsPhone: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12"><Divider align="left"><strong>Secondary Insurance</strong></Divider></div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Carrier</label>
                                <InputText id="sharedResidentSecondaryInsuranceName" value={profileForm.secondaryInsuranceName} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceName: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("secondaryInsuranceName")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsurancePlanType" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Plan Type</label>
                                <InputText id="sharedResidentSecondaryInsurancePlanType" value={profileForm.secondaryInsurancePlanType} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsurancePlanType: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceMemberId" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Member ID</label>
                                <InputText id="sharedResidentSecondaryInsuranceMemberId" value={profileForm.secondaryInsuranceMemberId} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceMemberId: e.target.value }))} className="w-full" />
                                {renderProfileDialogError("secondaryInsuranceMemberId")}
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceGroupNumber" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Group Number</label>
                                <InputText id="sharedResidentSecondaryInsuranceGroupNumber" value={profileForm.secondaryInsuranceGroupNumber} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceGroupNumber: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceSubscriberName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Subscriber Name</label>
                                <InputText id="sharedResidentSecondaryInsuranceSubscriberName" value={profileForm.secondaryInsuranceSubscriberName} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceSubscriberName: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceSubscriberRelationship" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Relationship</label>
                                <InputText id="sharedResidentSecondaryInsuranceSubscriberRelationship" value={profileForm.secondaryInsuranceSubscriberRelationship} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceSubscriberRelationship: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsurancePhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Phone</label>
                                <InputText id="sharedResidentSecondaryInsurancePhone" value={profileForm.secondaryInsurancePhone} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsurancePhone: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12 md:col-6">
                                <label htmlFor="sharedResidentSecondaryInsuranceClaimsPhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Claims Phone</label>
                                <InputText id="sharedResidentSecondaryInsuranceClaimsPhone" value={profileForm.secondaryInsuranceClaimsPhone} onChange={(e) => setProfileForm((prev) => ({ ...prev, secondaryInsuranceClaimsPhone: e.target.value }))} className="w-full" />
                            </div>
                            <div className="col-12">
                                <label htmlFor="sharedResidentInsuranceNotes" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Insurance Notes</label>
                                <InputTextarea
                                    id="sharedResidentInsuranceNotes"
                                    value={profileForm.insuranceNotes}
                                    onChange={(e) => setProfileForm((prev) => ({ ...prev, insuranceNotes: e.target.value }))}
                                    className="w-full"
                                    rows={4}
                                    autoResize
                                />
                            </div>
                        </div>
                    </div>
                </Dialog>

                <Dialog
                    header={renderSurfaceHeader("Previous Assessment Versions", "", "pi pi-history")}
                    visible={showAssessmentHistoryDialog}
                    modal
                    onHide={() => setShowAssessmentHistoryDialog(false)}
                    style={appDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                >
                    {assessmentForms.length === 0 ? (
                        <div style={appMutedTextStyle}>No saved assessment versions found yet.</div>
                    ) : (
                        <div className="flex flex-column gap-2">
                            {assessmentForms.map((form: any, index: number) => {
                                const isLatest = form.formId === latestAssessmentForm?.formId;
                                const normalizedFormId = String(form?.formId || "").trim();
                                return (
                                    <div key={normalizedFormId || index} style={{ ...appSectionCardStyle, padding: "0.95rem", borderRadius: 18, border: "1px solid #e2e8f0" }}>
                                        <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                                            <div>
                                                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                                    {form?.version ? `Version V${form.version}` : "Saved assessment version"}
                                                </div>
                                                <div style={{ ...appMutedTextStyle, marginTop: "0.25rem" }}>
                                                    {form?.updatedAt
                                                        ? `Last updated ${new Date(form.updatedAt).toLocaleString()}`
                                                        : form?.createdAt
                                                            ? `Created ${new Date(form.createdAt).toLocaleString()}`
                                                            : "Saved assessment version"}
                                                </div>
                                            </div>
                                            <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                                                <span style={{
                                                    ...getAssessmentStatusStyle(form.status),
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    borderRadius: 999,
                                                    padding: "0.2rem 0.6rem",
                                                    fontSize: "0.78rem",
                                                    fontWeight: 700,
                                                }}>
                                                    {getAssessmentStatusLabel(form.status)}
                                                </span>
                                                {isLatest ? (
                                                    <span style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderRadius: 999,
                                                        padding: "0.2rem 0.6rem",
                                                        fontSize: "0.78rem",
                                                        fontWeight: 700,
                                                        background: "#dcfce7",
                                                        color: "#166534",
                                                    }}>
                                                        Latest
                                                    </span>
                                                ) : null}
                                                <Button
                                                    type="button"
                                                    label={isLatest ? "Open Latest" : "Open"}
                                                    icon="pi pi-folder-open"
                                                    severity="secondary"
                                                    rounded
                                                    outlined
                                                    size="small"
                                                    onClick={() => handleOpenAssessmentVersion(normalizedFormId)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Dialog>

                <Dialog
                    header={renderSurfaceHeader("Previous Care Plan Versions", "", "pi pi-history")}
                    visible={showNcpHistoryDialog}
                    modal
                    onHide={() => setShowNcpHistoryDialog(false)}
                    style={appDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                >
                    {ncpForms.length === 0 ? (
                        <div style={appMutedTextStyle}>No saved care plan versions found yet.</div>
                    ) : (
                        <div className="flex flex-column gap-2">
                            {ncpForms.map((form: any, index: number) => {
                                const isLatest = form.formId === latestNcpForm?.formId;
                                const normalizedFormId = String(form?.formId || "").trim();
                                return (
                                    <div key={normalizedFormId || index} style={{ ...appSectionCardStyle, padding: "0.95rem", borderRadius: 18, border: "1px solid #e2e8f0" }}>
                                        <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                                            <div>
                                                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                                    Care Plan {form.version || ncpForms.length - index}
                                                </div>
                                                <div style={{ ...appMutedTextStyle, marginTop: "0.25rem" }}>
                                                    {form.updatedAt
                                                        ? `Last updated ${new Date(form.updatedAt).toLocaleString()}`
                                                        : form.createdAt
                                                            ? `Created ${new Date(form.createdAt).toLocaleString()}`
                                                            : "Saved nurse care plan version"}
                                                </div>
                                            </div>
                                            <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                                                <span style={{
                                                    ...getAssessmentStatusStyle(form.status),
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    borderRadius: 999,
                                                    padding: "0.2rem 0.6rem",
                                                    fontSize: "0.78rem",
                                                    fontWeight: 700,
                                                }}>
                                                    {getAssessmentStatusLabel(form.status)}
                                                </span>
                                                {isLatest ? (
                                                    <span style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderRadius: 999,
                                                        padding: "0.2rem 0.6rem",
                                                        fontSize: "0.78rem",
                                                        fontWeight: 700,
                                                        background: "#dcfce7",
                                                        color: "#166534",
                                                    }}>
                                                        Latest
                                                    </span>
                                                ) : null}
                                                <Button
                                                    type="button"
                                                    label={isLatest ? "Open Current" : "Open"}
                                                    icon="pi pi-folder-open"
                                                    severity="secondary"
                                                    rounded
                                                    outlined
                                                    size="small"
                                                    onClick={() => handleOpenNcpVersion(normalizedFormId)}
                                                />
                                                <Button
                                                    type="button"
                                                    label="Download"
                                                    icon="pi pi-download"
                                                    severity="secondary"
                                                    rounded
                                                    outlined
                                                    size="small"
                                                    loading={downloadingNcpFormId === normalizedFormId}
                                                    onClick={() => { void handleDownloadNcp(normalizedFormId); }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Dialog>

                <Dialog
                    header={renderSurfaceHeader("Start New Draft Automation", "", "pi pi-upload")}
                    visible={showNewAutomationDialog}
                    modal
                    onHide={closeNewAutomationDialog}
                    style={startDraftDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={startDraftDialogContentStyle}
                >
                    <div className="flex flex-column gap-3">
                        <>
                            <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                                <div style={{ maxWidth: "36rem" }}>
                                    <div style={{ fontWeight: 800, color: "#0f172a" }}>Build From Source Files</div>
                                    <div className="mt-2" style={{ ...appMutedTextStyle, lineHeight: 1.7 }}>
                                        Add up to {MAX_STAGED_FILES} source files, such as MAR sheets, assessments, and clinical records. Label them clearly, then prepare the draft assessment, care plan{generateDelegationDrafts ? ", delegation forms," : ""} and MAR sheet from one setup.
                                    </div>
                                    <div
                                        className="mt-3"
                                        style={{
                                            border: "1px solid #fdba74",
                                            background: "#fff7ed",
                                            borderRadius: 16,
                                            padding: "0.9rem 1rem",
                                            boxShadow: "0 10px 24px rgba(249, 115, 22, 0.08)",
                                        }}
                                    >
                                        <div style={{ fontSize: "0.74rem", fontWeight: 800, color: "#9a3412", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                            Important
                                        </div>
                                        <div className="mt-1" style={{ color: "#7c2d12", fontWeight: 700, lineHeight: 1.65 }}>
                                            Make sure all source documents are ready before you start. Each draft job creates a new version, so it is best to upload the complete set in one run.
                                        </div>
                                    </div>
                                </div>
                                {canShowProviderNurseCredits ? (
                                    <div style={getAutomationCreditsBadgeStyle(automationCreditsAvailable)}>
                                        Automation Credits: {automationCreditsAvailable}
                                    </div>
                                ) : null}
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                hidden
                                onChange={handleFileSelect}
                                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.tif,.tiff,.mp3,.wav,.m4a"
                            />

                            <div className="flex flex-wrap align-items-center justify-content-between gap-3" style={{
                                ...appFormSectionStyle,
                                margin: 0,
                                padding: "0.9rem 1rem",
                                borderRadius: 18,
                                background: "#f8fafc",
                                boxShadow: "none",
                            }}>
                                <div>
                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Source Files</div>
                                    <div className="mt-1" style={appMutedTextStyle}>
                                        {stagedFiles.length === 0
                                            ? "Add source files, then label each one before preparing the draft."
                                            : `${stagedFiles.length} of ${MAX_STAGED_FILES} files ready for review.`}
                                    </div>
                                    {canShowNurseUsageLeft ? (
                                        <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                                            Included this billing period: {nurseAssessmentsRemainingThisPeriod} assessment draft{nurseAssessmentsRemainingThisPeriod === 1 ? "" : "s"} remaining.
                                        </div>
                                    ) : null}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <Button
                                        data-tour="nurse-next-add-files"
                                        type="button"
                                        label={`Add Files (${stagedFiles.length}/${MAX_STAGED_FILES})`}
                                        icon="pi pi-upload"
                                        severity="secondary"
                                        rounded
                                        outlined
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={automationSubmitting || stagedFiles.length >= MAX_STAGED_FILES}
                                    />
                                    <Button
                                        data-tour="nurse-next-start"
                                        type="button"
                                        label="Prepare Draft"
                                        icon="pi pi-bolt"
                                        severity="warning"
                                        rounded
                                        onClick={() => { void handleStartAutomation(); }}
                                        disabled={automationSubmitting || stagedFiles.length === 0 || hasActiveAssessmentDraftJob}
                                    />
                                </div>
                            </div>

                            {hasActiveAssessmentDraftJob ? (
                                <div style={{ color: "#9a3412", fontWeight: 700 }}>
                                    An assessment draft job is already running for this resident. Wait for it to finish before starting another one.
                                </div>
                            ) : null}

                            {stagedFiles.length > 0 ? (
                                <div data-tour="nurse-next-tags" className="flex flex-column gap-3">
                                    {stagedFiles.map((staged) => (
                                        <div
                                            key={staged.id}
                                            style={{
                                                ...appFormSectionStyle,
                                                margin: 0,
                                                border: tagValidationRequested && !(staged.tags?.length) ? "1px solid #ef4444" : "1px solid #dbe7f3",
                                                borderRadius: 18,
                                                background: tagValidationRequested && !(staged.tags?.length) ? "#fef2f2" : "#ffffff",
                                                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)",
                                            }}
                                        >
                                            <div className="flex flex-wrap justify-content-between align-items-start gap-2">
                                                <div>
                                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{staged.file.name}</div>
                                                    <div className="mt-1" style={appMutedTextStyle}>
                                                        {(staged.file.size / (1024 * 1024)).toFixed(1)} MB
                                                    </div>
                                                </div>
                                                <Button
                                                    type="button"
                                                    icon="pi pi-times"
                                                    text
                                                    rounded
                                                    severity="secondary"
                                                    onClick={() => removeStagedFile(staged.id)}
                                                />
                                            </div>
                                            <div className="mt-3">
                                                <div style={{ fontSize: "0.74rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.55rem" }}>
                                                    File Labels
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {DOC_TAG_OPTIONS.map((option) => {
                                                        const isSelected = (staged.tags || []).includes(option.value);
                                                        return (
                                                            <Button
                                                                key={`${staged.id}-${option.value}`}
                                                                type="button"
                                                                label={option.label}
                                                                size="small"
                                                                rounded
                                                                severity={isSelected ? "warning" : "secondary"}
                                                                outlined={!isSelected}
                                                                onClick={() => toggleStagedTag(staged.id, option.value)}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                                {tagValidationRequested && !(staged.tags?.length) ? (
                                                    <div className="mt-2" style={{ color: "#b91c1c", fontSize: "0.82rem", fontWeight: 600 }}>
                                                        Choose at least one file label to continue.
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={appMutedTextStyle}>
                                    Add up to {MAX_STAGED_FILES} files, review that the set is complete, then choose a label for each file before preparing the draft.
                                </div>
                            )}
                        </>
                    </div>
                </Dialog>

                <Dialog
                    header={renderSurfaceHeader(
                        usesProviderAssessmentAutomation ? "Need More Access" : "Need More Access",
                        "",
                        "pi pi-wallet",
                    )}
                    visible={showFeatureAccessDialog}
                    onHide={() => {
                        setShowFeatureAccessDialog(false);
                        setAutomationSubmitting(false);
                    }}
                    style={appDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                    modal
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <div style={{ color: "#596F80", lineHeight: 1.7 }}>
                            {featureAccessDialogMessage}
                        </div>
                        <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fed7aa", borderRadius: 14, padding: "1rem" }}>
                            <div style={{ fontWeight: 700, color: "#9a3412" }}>Next</div>
                            <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
                                {usesProviderAssessmentAutomation
                                    ? (featureAccessDialogAvailableCredits > 0
                                        ? `You currently have ${featureAccessDialogAvailableCredits} nurse drafting credit${featureAccessDialogAvailableCredits === 1 ? "" : "s"} available.`
                                        : "Buy Automation Credit to keep going.")
                                    : "Open Billing to keep going."}
                            </div>
                        </div>
                        <div className="flex flex-wrap justify-content-end gap-2">
                            {featureAccessDialogAllowUseCredit && featureAccessDialogUseCreditAction ? (
                                <Button
                                    type="button"
                                    label="Use 1 Credit"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => {
                                        setShowFeatureAccessDialog(false);
                                        setAutomationSubmitting(false);
                                        featureAccessDialogUseCreditAction();
                                    }}
                                />
                            ) : null}
                            {(!usesProviderAssessmentAutomation || featureAccessDialogAvailableCredits <= 0) ? (
                                <Button
                                    type="button"
                                    label={usesProviderAssessmentAutomation ? "Buy Now" : "Open Billing"}
                                    severity="warning"
                                    rounded
                                    onClick={() => {
                                        setShowFeatureAccessDialog(false);
                                        setAutomationSubmitting(false);
                                        navigate(usesProviderAssessmentAutomation ? `${RouterAddress.BILLING}?tab=one-time` : RouterAddress.BILLING);
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </Dialog>

                <Dialog
                    header={renderSurfaceHeader("Drafting Progress", "", "pi pi-sync")}
                    visible={showProcessingDialog}
                    modal
                    onHide={handleCloseProcessingDialog}
                    style={appDialogStyle}
                    headerStyle={appDialogHeaderStyle}
                    contentStyle={appDialogContentStyle}
                >
                    {!shouldShowProcessingDialogContent ? (
                        <div style={appFormSectionStyle}>
                            <div style={appMutedTextStyle}>
                                No documents are currently queued or processing.
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-column gap-3">
                            <div style={{
                                ...appNeutralPanelStyle,
                                border: isProcessingCompleted
                                    ? "1px solid #86efac"
                                    : uploadProgress.status === "failed" && !processingJobDetails
                                        ? "1px solid #fdba74"
                                        : "1px solid #dbe7f3",
                                background: isProcessingCompleted
                                    ? "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)"
                                    : uploadProgress.status === "failed" && !processingJobDetails
                                        ? "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)"
                                        : undefined,
                                margin: 0,
                            }}>
                                <div className="flex flex-column gap-3">
                                    <div>
                                        <div className="flex align-items-center gap-3" style={{ marginBottom: "0.45rem" }}>
                                            {((uploadProgress.status === "uploading") || (!isProcessingCompleted && !isProcessingFailed && !!processingJobDetails)) ? (
                                                <ProgressSpinner
                                                    style={{ width: 34, height: 34, flexShrink: 0 }}
                                                    strokeWidth="4"
                                                    animationDuration=".8s"
                                                />
                                            ) : null}
                                            <div style={{
                                                fontWeight: 800,
                                                color: isProcessingCompleted
                                                    ? "#166534"
                                                    : uploadProgress.status === "failed" || isProcessingFailed
                                                        ? "#9a3412"
                                                        : "#0f172a",
                                            }}>
                                                {uploadProgress.status === "uploading"
                                                    ? "Uploading source files"
                                                    : isProcessingCompleted
                                                        ? "Draft is ready"
                                                        : uploadProgress.status === "failed" && !processingJobDetails
                                                            ? "Upload finished with issues"
                                                            : isProcessingFailed
                                                                ? "Draft preparation finished with issues"
                                                                : "Draft is being prepared"}
                                            </div>
                                        </div>
                                        <div style={appMutedTextStyle}>
                                            {uploadProgress.status === "uploading"
                                                ? uploadProgress.currentFileName
                                                    ? `Uploading ${uploadProgress.currentFileName}. Keep this dialog open if you want to follow progress live.`
                                                    : "Your source files are being uploaded now."
                                                : isProcessingCompleted
                                                    ? generatedDelegationForms.length > 0
                                                        ? "You can close this now and open the new draft documents."
                                                        : "You can close this now and review the new draft."
                                                    : uploadProgress.status === "failed" && !processingJobDetails
                                                        ? uploadProgress.errorMessage || "One or more source files could not be uploaded."
                                                        : isProcessingFailed
                                                            ? "You can try again or continue by hand."
                                                            : `Current status: ${formatProcessingStatus(processingJobDetails?.jobSubStatus || processingJobDetails?.jobStatus)}`}
                                        </div>
                                    </div>
                                    <div className="grid" style={{ margin: 0 }}>
                                        {processingSummaryItems.map((item) => (
                                            <div key={item.label} className="col-12 md:col-4" style={{ paddingTop: "0.35rem", paddingBottom: 0 }}>
                                                <div style={{ ...appFormSectionStyle, margin: 0, padding: "0.85rem 0.95rem", borderRadius: 16, boxShadow: "none" }}>
                                                    <div style={dialogMetricLabelStyle}>{item.label}</div>
                                                    <div style={{ fontWeight: 700, color: "#0f172a", marginTop: "0.35rem" }}>
                                                        {item.value}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {shouldShowUploadProgressSection ? (
                                <div style={appFormSectionStyle}>
                                    <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.75rem" }}>Upload Progress</div>
                                    <div className="grid" style={{ rowGap: "0.6rem" }}>
                                        <div className="col-6 md:col-4">
                                            <div style={dialogMetricLabelStyle}>Uploaded</div>
                                            <div style={{ fontWeight: 700, color: "#166534" }}>{uploadProgress.successfulFiles}</div>
                                        </div>
                                        <div className="col-6 md:col-4">
                                            <div style={dialogMetricLabelStyle}>Failed</div>
                                            <div style={{ fontWeight: 700, color: uploadProgress.failedFiles.length > 0 ? "#c2410c" : "#0f172a" }}>{uploadProgress.failedFiles.length}</div>
                                        </div>
                                        {uploadProgress.currentFileName ? (
                                            <div className="col-12 md:col-4">
                                                <div style={dialogMetricLabelStyle}>Current File</div>
                                                <div style={{ ...appMutedTextStyle, color: "#334155" }}>{uploadProgress.currentFileName}</div>
                                            </div>
                                        ) : null}
                                    </div>
                                    {uploadProgress.failedFiles.length > 0 ? (
                                        <div style={{ marginTop: "0.85rem" }}>
                                            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>
                                                Failed uploads
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {uploadProgress.failedFiles.map((fileName, index) => (
                                                    <span
                                                        key={`${fileName}-${index}`}
                                                        style={getStatusChipStyle("warning")}
                                                    >
                                                        {fileName}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                    {uploadProgress.errorMessage ? (
                                        <div style={{ ...appMutedTextStyle, marginTop: "0.75rem", color: "#9a3412" }}>
                                            {uploadProgress.errorMessage}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {(successfulSections.length > 0 || pendingSections.length > 0) ? (
                                <div style={appFormSectionStyle}>
                                    <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.75rem" }}>Assessment Sections</div>
                                    {successfulSections.length > 0 ? (
                                        <div style={{ marginBottom: pendingSections.length > 0 ? "0.85rem" : 0 }}>
                                            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#166534", marginBottom: "0.45rem" }}>
                                                Completed
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {successfulSections.map((section: any, index: number) => (
                                                    <span
                                                        key={`${section?.recordType || section?.description || "completed"}-${index}`}
                                                        style={getStatusChipStyle("success")}
                                                    >
                                                        {getSectionDisplayName(section)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                    {pendingSections.length > 0 ? (
                                        <div>
                                            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#92400e", marginBottom: "0.45rem" }}>
                                                Pending
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {pendingSections.map((section: any, index: number) => (
                                                    <span
                                                        key={`${section?.recordType || section?.description || "pending"}-${index}`}
                                                        style={getStatusChipStyle("warning")}
                                                    >
                                                        {getSectionDisplayName(section)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {delegationDraftGenerationRequested ? (
                                <div style={appFormSectionStyle}>
                                    <div className="flex align-items-center gap-2" style={{ marginBottom: "0.75rem" }}>
                                        {isDelegationDraftGenerationInProgress ? (
                                            <ProgressSpinner
                                                style={{ width: 22, height: 22, flexShrink: 0 }}
                                                strokeWidth="5"
                                                animationDuration=".8s"
                                            />
                                        ) : null}
                                        <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                            Delegation Drafts
                                        </div>
                                    </div>
                                    {generatedDelegationForms.length > 0 ? (
                                        <>
                                            <div style={{ ...appMutedTextStyle, marginBottom: "0.75rem" }}>
                                                New draft delegation forms were created from this guided draft.
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {generatedDelegationForms.map((form: any, index: number) => (
                                                    <span
                                                        key={`${form?.formId || form?.formCode || "delegation"}-${index}`}
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            borderRadius: 999,
                                                            padding: "0.25rem 0.65rem",
                                                            fontSize: "0.78rem",
                                                            fontWeight: 700,
                                                            background: "#eff6ff",
                                                            color: "#1d4ed8",
                                                            border: "1px solid #bfdbfe",
                                                        }}
                                                    >
                                                        {String(form?.formTitle || form?.formCode || "Delegation Form").trim()}
                                                        {form?.version ? ` • v${form.version}` : ""}
                                                    </span>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div style={appMutedTextStyle}>
                                            {isDelegationDraftGenerationInProgress
                                                ? "Delegation draft forms are being generated now."
                                                : delegationDraftGenerationStatus === "failed"
                                                    ? "The draft finished, but the delegation forms were not created."
                                                    : delegationDraftGenerationStatus === "completed"
                                                        ? "No delegation draft forms were created from this run."
                                                        : "Delegation draft forms will be prepared after the assessment is completed successfully."}
                                        </div>
                                    )}
                                </div>
                            ) : null}
                            <div style={{
                                borderTop: "1px solid #e2e8f0",
                                paddingTop: "0.85rem",
                            }}>
                                {showMarkAsFailedAction ? (
                                    <div className="flex justify-content-end mb-3">
                                        <Button
                                            type="button"
                                            label="Mark as Failed"
                                            icon="pi pi-stop-circle"
                                            severity="danger"
                                            rounded
                                            outlined
                                            onClick={() => { void handleMarkProcessingFailed(); }}
                                            disabled={automationSubmitting}
                                        />
                                    </div>
                                ) : null}
                                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.4rem", fontSize: "0.9rem" }}>Automation Notes</div>
                                <div style={appMutedTextStyle}>{AI_MESSAGE_FOR_COMPLIANCE}</div>
                            </div>
                        </div>
                    )}
                </Dialog>
            </div>
        </div>
    );
};

export default NurseResidentProfilePage;
